import React, { useState, useEffect } from "react";
import { NO_AUTOFILL, noAutofillClass } from "../../lib/noAutofill"; // 213B §18 — no sign-ins, no contacts, in a modal's field
import type { CSSProperties } from "react";
import { Lock, Download, Trash2, Pencil, RotateCcw, Ban, FileText } from "lucide-react";
import {
  ModalLayout,
  SaveButton,
  CancelButton,
  SegmentedControl,
} from "../SharedUI";
import AmountField from "../AmountField"; // 208 §10 — the one amount field (the pro forma's money; NumField keeps its two percentages)
import { I, SK, fc, fq, fmin, fd, nid, pname, pnameIn, firmCurrencyOf } from "../../utils";
import { clientDocumentLanguage } from "../../lib/docLang.mjs"; // 208 §11 [RULED 9], 209 §5 [RULED 18] — the client's language, else its case's
import { openMatterForm, languageRefusalAction } from "../../lib/caseLanguage"; // 209 §5 — the case form, one click away
import { periodLabel } from "../../lib/periodLabel.mjs"; // 206E §4.3 — the period label, one definition both ends read
import { tickedIn, drawsIn, seedTicks, servedSourceFigures } from "../../lib/invoiceSources";
import type { SourceTicks } from "../../lib/invoiceSources";
import type { Translate } from "../../lib/interestRefusal"; // 207 §11 — the quarter's word, narrowed at its seam
import { firmAddressIn } from "../../lib/firmAddress.mjs"; // 207 §12 — the firm's address in the reader's language
import { openTabNow, landTab, pdfBlobUrl, draftOutcomeOf } from "../../lib/proFormaTabs"; // 213B §12 / §13 — the tab at the click
import { joinNames, initialTickedIds, tickedInOrder, addresseesOf, firstTickedIndexOf, clientRowsOf, asRows, toggleId, editSnapshotOf, missingEmailsOf } from "../../lib/proFormaAddressees"; // 213B §11 [RULED 44] — the addressees, read through unknown

/* 213B §11 — the two words the addressees add (the refusal when none is ticked, the note when a ticked client has no
   e-mail) are said through the app's own toast, typed on Window (global.d.ts), this file's `toast` prop being untyped
   (emailPdfLink.ts's way, so the file's ESLint count does not rise). */
const sayPf = (msg: string, kind: string): void => { (globalThis as { lxToast?: NonNullable<Window["lxToast"]> }).lxToast?.(msg, kind); };
import { useTaxonomy } from "../../hooks/useTaxonomy"; // 207 §12 — the firm's country as a word
import { resolveTaxonomyLabel } from "../../lib/taxonomyHelpers";
import { getInvoice, createProForma, computeProForma, getInvoiceSources } from "../../api";
import { useTranslation, tImperative as _tImp } from "../../i18n";
import { usePermissions } from "../../hooks/usePermissions";
import {
  getLockTier,
  getLockTierLabel,
  isFieldEditable,
} from "../../lib/invoiceLock";
import {
  cancelProForma,
  uncancelProForma,
  approveProForma, // 179 B.7
  sendToBillit,
  sendProFormaToClient,
  generateProForma,
  getFactuPdf,
  editProForma,
  softDeleteInvoice,
  trashReasonToMessage,
  postRestoreInvoice,
} from "../../lib/invoiceFetch";
import { getStatusBadgeTooltip } from "../../lib/invoiceTransitions";
import { invoiceStatusWord } from "../../lib/invoiceStatusWord"; // 209 §16 — one document's status, in words
// 191B §A (item 8) — the particular conditions print the success-fee rate IN
// FORCE: the stored rate (a stored 0 prints 0), else the one default the money
// counts with and the form displays (src/lib/successFee.mjs). Before: `||
// "—"`, which printed "—" for a null AND for a stored 0.
import { successFeeRateOf } from "../../lib/successFee.mjs";
import { RAD, FS } from "../../lib/tokens";
import MenuSelect from "../MenuSelect"; // 154B §2
import { useConfirm } from "../useConfirmAndUndo";
import Table from "../Table";
import { ListNotLoaded } from "../SharedUI"; // 212 §22 — the sources, when they could not load
import { useReadMark, usePageListContext } from "../../hooks/usePageList";
import { ListLoadContext, PAGE_LISTS } from "../../lib/listLoad";
import { withCount } from "../../lib/pluralVars"; // 209 §7 — a sentence's number, read once, as its placeholder and as `count`

// 179 C.3 — `BillitOrderIdInput` (the inline order-id editor of the push-locked
// state, PUT /:id/billit-order-id) STOOD HERE: a state no live path reached.

// Status-aware label used for the modal title, the header right-block
// badge, and the PDF P3 hardcoded layout. Under Phase M+3 Gate 4 C5
// the (paymentStatus, billitSubmittedAt) pair drives the label —
// invoices with billitSubmittedAt set surface as "INVOICED — X" per
// scope v2.1 §3.3, regardless of payment progression.
// Phase 35 Stage-3 — the document is always a "pro forma invoice". The only
// document-title distinction is active vs cancelled; payment state (unpaid /
// partly paid / paid / overdue) is a separate attribute shown elsewhere, never
// the document title. Never "DRAFT INVOICE" / "Invoice" / mixed caps.
export function getInvoiceHeaderLabel(invoice: any) {
  if (invoice && invoice.paymentStatus === "cancelled")
    return _tImp("proforma.header.cancelled_pro_forma", {
      fallback: "CANCELLED PRO FORMA",
    });
  return _tImp("proforma.header.pro_forma", { fallback: "PRO FORMA" });
}

// BUG-A SWEEP — hoisted out of ProFormaModal's render body. It was declared
// there and mounted as <Lbl2 …/>, which made it a NEW component type on every
// render of the modal: React discarded the <span> and built a fresh one each
// time. Nothing here holds state, so nothing visible was lost — but the type
// churn is the same defect that cost the dashboard, the Costs view and the
// client importer, and a rule cannot forbid the shape only where it hurt.
// A pure move: it closes over nothing but FS, which is a module import.
const Lbl2 = ({ children }: any) => (
  <span
    style={{
      fontSize: FS.s9,
      color: "var(--tm)",
      textTransform: "uppercase",
      letterSpacing: ".4px",
      fontWeight: 600,
    }}
  >
    {children}
  </span>
);

// BUG-A SWEEP — hoisted out of ProFormaModal's render body, same shape and
// same reasoning as Lbl2 above. Kept as a separate declaration rather than
// merged with it: this commit MOVES, it does not improve (SESSION_RULES rule 3
// — moving and bettering are two commits, never one). Collapsing the two
// near-identical labels is a later, ruled act.
const Lbl = ({ children }: any) => (
  <span
    style={{
      fontSize: FS.s9,
      color: "var(--tm)",
      textTransform: "uppercase",
      letterSpacing: ".4px",
      fontWeight: 600,
    }}
  >
    {children}
  </span>
);

// 161B §2 — THE FIELD OWNS ITS TEXT.
//
// THE BUG: `<input type="number" value={form.retainerAmount}
// onChange={e => uf("retainerAmount", +e.target.value || 0)}>`. The form holds
// a NUMBER, so clearing the box sends `+"" || 0` -> 0, the form re-renders `0`
// into it, and the caret lands AFTER that zero. Every subsequent keystroke
// appends: type 500 into an emptied field and you get 0500, or you fight the
// zero. It cannot be fixed by a better parse, because the defect is that a
// number is the wrong thing for an input to be VALUE'd by — "" and 0 are
// different editing states and a number cannot tell them apart.
//
// THE SHAPE: this component keeps the TEXT (a string, possibly empty) and
// reports the parsed NUMBER upward. Empty or unparseable is `0` to the form
// and EMPTY on screen — so a cleared field stays cleared and the Save gate
// (`Number(form.retainerAmount) > 0`) still refuses it. On blur the text is
// re-formatted from the committed number, where `clamp` (if the caller gave
// one) becomes visible.
//
// 191 fold-in 7 [RULED, LAURENT, 2026-09-17] — A MONEY FIELD NEVER OPENS WITH
// A ZERO UNDER THE CARET. "Facturer une provision" opened reading "0" (this
// component initialised its text to String(value ?? 0) over a form whose
// retainerAmount is 0, mounted autoFocus) and typing 14000 gave "014000" —
// the stuck zero at OPEN, the one 161B §2 cured at CLEAR. The rule 161B
// wrote, "0 to the form, EMPTY on screen", now holds at every moment: a
// committed 0 is an EMPTY box with a greyed "0" placeholder [JUDGEMENT on the
// placeholder] — at open, while the form follows a prefill, and after blur —
// while the form still receives 0 and every Save gate is unchanged. The
// other mounts (the stated fee, the commercial deduction, the per-source
// draw) take it by construction; the costs ratio and VAT % inputs (the two
// pre-161B `+e.target.value || 0` inputs this modal still had) joined
// NumField in the commit after this one.
//
// 208 §10 — ITS FOUR MONEY MOUNTS MOVED TO THE ONE AMOUNT FIELD
// (src/components/AmountField.tsx: « 14 000,00 », a decimal comma), which took
// this field's rules — 0 an empty box with the grey « 0 », 0 to the form when
// emptied, a ceiling shown as it bites (`max`), the retainer's autofocus. This
// field keeps the two percentages, the costs ratio and VAT %.
//
// A COMPONENT RATHER THAN A HOOK, deliberately: the second call site is the
// per-source amount input inside `(sourceDocs||[]).map(…)`, and a hook there
// would be a hook in a loop. Module scope, so it has a stable identity —
// scripts/lint-inline-components.js forbids the other thing.
// 169 R4 — every ISO 4217 code this runtime knows, for the create-time
// currency picker (the firm's is listed first by the field).
const PF_CURRENCY_OPTIONS: ReadonlyArray<readonly [string, string]> = (() => {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  let codes = ["EUR", "USD", "GBP", "CHF"];
  try {
    if (typeof intl.supportedValuesOf === "function") codes = intl.supportedValuesOf("currency");
  } catch {
    /* the fallback list stands */
  }
  return codes.map((c) => [c, c] as const);
})();

/* 208 §10 — THE ZERO RULE THE FOUR AMOUNT MOUNTS SHARE (191 fold-in 7, as this
   field keeps it): a 0 — or nothing — reaches the amount field as nothing, so
   it draws an empty box with its grey « 0 »; any other amount as it is. */
const zeroEmpty = (v: unknown): unknown => (Number(v) === 0 ? null : v);

function NumField({
  value,
  onCommit,
  clamp,
  className = "field-input",
  style,
  min,
  max,
  step,
  autoFocus,
}: {
  value: number;
  onCommit: (n: number) => void;
  clamp?: (n: number) => number;
  className?: string;
  // 161B §2 — typed, not `any`: the ratchet caught this file's counter growing
  // 66 -> 67 and SESSION_RULES is explicit that new code gets a type rather
  // than a new baseline (re-recording is for a file MOVE). CSSProperties is
  // what every caller passes and what the <input> takes.
  style?: CSSProperties;
  min?: number;
  max?: number;
  step?: number;
  autoFocus?: boolean;
}) {
  // 0 to the form, EMPTY on screen — one spelling for the three moments.
  const shown = (n: number | null | undefined): string => (n == null || n === 0 ? "" : String(n));
  const [text, setText] = useState<string>(() => shown(value));
  const [editing, setEditing] = useState(false);
  // While the box is not being edited it follows the form — a prefill, a
  // source pick, a clamp applied elsewhere. While it IS being edited it does
  // not, or the parse would fight the typing.
  useEffect(() => {
    if (!editing) setText(shown(value));
  }, [value, editing]);
  return (
    <input
      className={className}
      type="number"
      min={min}
      max={max}
      step={step}
      style={style}
      value={text}
      placeholder="0"
      autoFocus={autoFocus}
      onFocus={() => setEditing(true)}
      onChange={(e) => {
        const raw = e.target.value;
        const n = parseFloat(raw);
        const parsed = Number.isFinite(n) ? n : 0;
        const settled = clamp ? clamp(parsed) : parsed;
        // The text follows the keystrokes EXCEPT where the clamp actually
        // bit — otherwise the box would show 999 while the ladder priced the
        // ceiling, and only blur would reconcile them. This does not bring the
        // stuck zero back: an empty box parses to 0, and 0 is inside every
        // clamp these call sites use, so `settled === parsed` and the empty
        // text stands. Where the clamp bites down to 0 the box empties, as it
        // does everywhere else a 0 is committed.
        setText(settled === parsed ? raw : shown(settled));
        onCommit(settled);
      }}
      onBlur={() => {
        setEditing(false);
        const n = parseFloat(text);
        const parsed = Number.isFinite(n) ? n : 0;
        const settled = clamp ? clamp(parsed) : parsed;
        setText(shown(settled));
        onCommit(settled);
      }}
    />
  );
}

export default function ProFormaModal({
  pfMod,
  invoiceId,
  onClose,
  onRestored,
  onRemake,
  te,
  sTe,
  ca,
  cc,
  cl,
  co,
  inv,
  sInv,
  s,
  toast,
  tkTypes,
  tk,
  sTk,
  firmSettings,
  emailClient,
}: any) {
  // 179 B.6 — `useTranslation()` hands back the context, typed `any`
  // (i18n.tsx, `createContext<any>`), so every `t()` here counted as an unsafe
  // call under the eslint ratchet and any NEW word would have raised the
  // file. Narrowed to the function's real contract at the one place it is
  // taken; nothing at runtime changes.
  const { t } = useTranslation() as { t: (key: string, vars?: Record<string, unknown>) => string };
  const askConfirm = useConfirm();
  // Resolver for firmSettings fields. Bank accounts live under
  // `bankAccounts` in FirmSettings (the legacy localStorage key was
  // `banks` — that alias is retired with A2).
  const getInvSettings = () => firmSettings || {};
  // 169 G2 — the notes DRAFT is gone with the textarea that owned it: the view
  // arm renders `viewInv.notes` directly, so there is no local copy to seed, to
  // keep in sync, or to lose. C5 renames the in-flight flag from `submitting`
  // to `pushing` to match the v2.1 vocabulary (Push to Billit, not Submit).
  const [pushing, setPushing] = useState(false);
  const [approving, setApproving] = useState(false); // 179 B.7
  const [transitioning, setTransitioning] = useState(false);
  // Gate 4 C3b D3-A — Send Pro Forma in-flight flag.
  const [sending, setSending] = useState(false);
  // Phase 40 — pro-forma PDF (client 04. FACTU). factuPdf holds the immutable
  // version list (probed on open, prod-only via Graph); selVersion is the chosen
  // version's webUrl for "Show PDF"; genBusy guards Generate. 169 G4 — the
  // `genLang` override is gone with its select: the document's language is the
  // client's, and the server resolves it.
  const [factuPdf, setFactuPdf] = useState<any>({ versions: [], latestWebUrl: null });
  /* 213B §12 — THE GENERATED PREVIEW, kept for the window's life: « Générer le
     PDF » leaves « Afficher le PDF » behind, which opens the same blob with no
     second round trip; nothing is saved (D9 stands), closing the window forgets
     it and releases the blob. */
  const [previewPdf, setPreviewPdf] = useState<{ url: string } | null>(null);
  useEffect(() => () => { if (previewPdf) URL.revokeObjectURL(previewPdf.url); }, [previewPdf]);
  const [selVersion, setSelVersion] = useState("");
  const [genBusy, setGenBusy] = useState(false);
  // Phase 40 (C2.4) — preview reconciliation: PDF vs filled Peppol XML.
  const [xmlRecon, setXmlRecon] = useState<any>(null);
  // Gate 4 C4 — Trash in-flight flag.
  const [trashing, setTrashing] = useState(false);
  // Phase M+3.5 D4 — Restore-from-trash in-flight flag (modal-side, mirrors
  // the row-level flag in InvoiceView). Active when invoiceId points at a
  // soft-deleted row.
  const [restoring, setRestoring] = useState(false);
  // Phase M+3.5 D4 — fetched invoice for view mode. The App-level `inv`
  // prop excludes trashed rows; fetching by id via GET /api/invoices/:id
  // (already returns trashed rows) lets the modal preview them.
  const [fetchedInv, setFetchedInv] = useState<any>(null);
  const { can: clientCan, viewer } = usePermissions();

  // Phase M+3.5 D4 — fetch the invoice when invoiceId is set. Resets
  // fetchedInv when the prop changes so we don't show stale data when
  // the modal is reopened on a different row.
  useEffect(() => {
    if (invoiceId == null) {
      setFetchedInv(null);
      return;
    }
    let cancelled = false;
    getInvoice(invoiceId)
      .then((row) => {
        if (!cancelled) setFetchedInv(row);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error("[ProFormaModal] getInvoice failed", e);
        toast(
          t("proforma.toast.load_failed", {
            msg: e.message || t("common.unknown_error", { fallback: "unknown error" }),
            fallback: "⚠ Failed to load invoice: {msg}",
          }),
          "error",
        );
        onClose && onClose();
      });
    return () => {
      cancelled = true;
    };
  }, [invoiceId]);

  // Phase 40 — probe the saved FACTU pro-forma PDF so the action shows
  // "Show PDF" when one exists (else "Generate PDF"). Prod-only (Graph); the
  // staging stub answers not-connected and the probe leaves exists=false.
  useEffect(() => {
    if (invoiceId == null) {
      setFactuPdf({ versions: [], latestWebUrl: null });
      return;
    }
    let cancelled = false;
    getFactuPdf(invoiceId)
      .then((r) => {
        if (!cancelled && r && r.ok) setFactuPdf({ versions: r.versions || [], latestWebUrl: r.latestWebUrl || null });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [invoiceId]);

  // ============ READ-ONLY VIEW MODE ============
  // Phase M+3.5 D4 — view mode is triggered by the `invoiceId` prop.
  // The modal fetches the invoice via GET /:id (supporting trashed rows).
  // Legacy `pfMod.viewInvoiceId` field is no longer consulted.
  if (invoiceId) {
    const viewInv = fetchedInv;
    if (!viewInv) {
      // Fetch in flight — render a minimal loading shell so the modal
      // doesn't flash before the data arrives.
      return (
        <ModalLayout
          title={t("common.loading", { fallback: "Loading…" })}
          onClose={onClose}
          maxWidth={680}
        >
          <div
            style={{
              padding: 40,
              textAlign: "center",
              color: "var(--tm)",
              fontSize: FS.s11,
            }}
          >
            {t("proforma.loading_invoice", { fallback: "Loading invoice…" })}
          </div>
        </ModalLayout>
      );
    }
    const viewEntries = te.filter((e: any) =>
      (viewInv.entries || []).includes(e.id),
    );
    const viewSettings = getInvSettings();
    const viewBank =
      (viewSettings.bankAccounts || [])[viewInv.bankAccountIdx] || null;
    const actName2 = (code: any) => {
      const t = (tkTypes || []).find((x: any) => x.code === code);
      return t ? t.desc : code || "—";
    };
    const totalMin = viewEntries.reduce((sum: any, e: any) => sum + (e.min || 0), 0);

    // 179 C.2 — `isReady` / `isDraft` STOOD HERE, unread: those two states have
    // been unreachable since Phase 35 made every new pro forma `unpaid`.
    const isCancelled = viewInv.paymentStatus === "cancelled";
    // 209 §14 [RULED 19] — approval is a gate before sending for every firm: the
    // switch this window read (179 B's ruling 9) is gone, so the pill, the Approve
    // button and the freeze below always hold.
    const isApproved = !!(viewInv as { approvedDate?: string | null }).approvedDate;
    // 209 §15 [ADVISOR] — SENT WHILE UNAPPROVED: before approval existed (179), or while
    // the switch 209 §14 removed was off. The approve route refuses a sent document
    // (« Approval must come before sending »), so such a document can never be approved:
    // it reads « Envoyé sans approbation » — in its pill, and where « Approuver » stood —
    // and offers no approval. "Sent" is the approve route's own test.
    const sentView = viewInv as { sentToClientAt?: string | null; sentToPeppolAt?: string | null; billitSubmittedAt?: string | null };
    const sentUnapproved = !isApproved && [sentView.sentToClientAt, sentView.sentToPeppolAt, sentView.billitSubmittedAt].some(Boolean);
    let approvalWord = t("invoice.approval.pending", { fallback: "Approval" });
    if (isApproved) approvalWord = t("invoice.approval.approved", { fallback: "Approved" });
    else if (sentUnapproved) approvalWord = t("invoice.approval.sent_unapproved", { fallback: "Sent without approval" });
    // 179 B.7 [RULED, ruling 7] — the approve right, resolved by the client's
    // mirror of can() (role default or live per-case grant; admin overlay).
    // A courtesy: the server refuses on its own (B.3, B.4, B.5).
    const canApprove = clientCan("caseView.proformas.approve", {
      caseId: viewInv.caseId,
      ccId: viewInv.ccId,
    });
    const approveTargetId = (viewInv as { id: number }).id;

    // Gate 4 C1 — flags that drive Push to Billit / UBL re-download / lock UX.
    // `lateral.invoices` is the section key the server enforces at
    // requireSectionForRow on /submit, /ubl, /restore, etc. Pass both
    // caseId and ccId so collective-case invoices resolve correctly.
    const viewerCanInvoices = clientCan("lateral.invoices.view", {
      caseId: viewInv.caseId,
      ccId: viewInv.ccId,
    });
    // Gate 4 C3b D6 α — two-tier lock. tier ∈ {'none','sent-locked','push-locked'}.
    const lockTier = getLockTier(viewInv);
    const locked = lockTier !== "none";
    const lockTierLabel = getLockTierLabel(lockTier);
    // 179 C.3 — `editableFields` STOOD HERE; its only reader was the push-locked block.
    // Phase M+3.5 D4 — trashed (soft-deleted) preview mode. Forces all
    // fields to read-only regardless of lock tier and replaces the
    // footer with Close + Restore. The lock-tier predicates still
    // compute normally; isTrashedView is the master gate above them.
    const isTrashedView = !!viewInv.deletedAt;
    const fieldEditable = (inv2: any, field: any) =>
      isTrashedView ? false : isFieldEditable(inv2, field);
    // 179 C.3 — `canDownloadUbl` STOOD HERE (the UBL of the removed push).

    const refetchInvoice = async () => {
      try {
        const fresh = (await getInvoice(viewInv.id)) as
          | ({ id?: number | null } & Record<string, unknown>)
          | null;
        if (fresh && fresh.id != null) {
          // Phase M+3.5 D4 — fetchedInv is the source of truth for view
          // mode. Update both it and the App-level list so any consumer
          // that still reads from `inv` stays consistent.
          setFetchedInv(fresh);
          if (sInv)
            sInv((arr: any) =>
              (arr || []).map((i: any) =>
                i.id === fresh.id ? { ...i, ...fresh } : i,
              ),
            );
        }
      } catch (e) {
        // Non-fatal — list will rehydrate on next page load. Log only.
        console.error("[ProFormaModal] refetchInvoice failed", e);
      }
    };

    // Phase 35 Stage-3 r2 (§3) — "Send to Peppol" (scaffold): sets To Peppol =
    // Sent. No real transmission (deferred until Billit credentials arrive).
    // 179 C.3 — the v2.1 push (/submit, lineItems, billitSubmittedAt) it
    // replaced is gone from the server, and so is the scaffold's UBL attempt.
    const handlePushToBillit = async () => {
      if (pushing) return;
      askConfirm({
        // 161C §3 — the document's own number, as on the cancel dialog.
        title: t("confirm.send_peppol_title", {
          number: viewInv.number,
          fallback: `Send ${viewInv.number} to Peppol?`,
        }),
        message: t("proforma.send_to_peppol_confirm", {
          fallback:
            "Send this pro forma to Peppol? It will be marked Sent to Peppol.",
        }),
        onConfirm: async () => {
          setPushing(true);
          try {
            const result = await sendToBillit(viewInv.id);
            if (result.ok) {
              await refetchInvoice();
              if (sInv && result.invoice)
                sInv((arr: any) => (arr || []).map((i: any) => (i.id === viewInv.id ? result.invoice : i)));
              toast(
                t("proforma.sent_to_peppol_toast", {
                  fallback: "Sent to Peppol — To Peppol marked Sent.",
                }),
              );
            } else {
              toast(
                `⚠ ${result.error || t("proforma.toast.send_peppol_failed", { fallback: "Send to Peppol failed." })}`,
                "error",
              );
            }
          } finally {
            setPushing(false);
          }
        },
      });
    };

    // Phase 40 → 213B §12 [ADVISOR] — the tab opens AT THE CLICK and lands when the answer comes: src/lib/proFormaTabs.ts

    // Phase 40 — "Send to client": the SERVER generates the pro-forma PDF, saves
    // it to the client 04. FACTU folder, drafts the Outlook email with it
    // attached, and marks To Client = Sent. 169 G4 — the language IS
    // Client.language; there is no override left to pass. Prod-only (Graph):
    // on staging this returns an error.
    const handleSendToClient = async () => {
      if (sending) return;
      setSending(true);
      // 213B §13 — the tab for the Outlook draft opens AT THE CLICK (above); it lands on `draftWebLink` when the
      // send answers, and closes on a refusal, a thrown call or an answer without a link
      const tab = openTabNow();
      try {
        // 213B §11 [RULED 44] — a ticked client with no e-mail is left out of the draft, and the window says so first
        const missing = missingEmailsOf(viewInv);
        if (missing.length) sayPf(t("proforma.addressees.no_email", { names: missing.join(", "), fallback: `${missing.join(", ")}: no e-mail address — the draft will not be addressed to them.` }), "warning");
        const result = await sendProFormaToClient(viewInv.id, {});
        if (result.ok) {
          await refetchInvoice();
          if (sInv && result.invoice) sInv((arr: any) => (arr || []).map((i: any) => (i.id === viewInv.id ? result.invoice : i)));
          const sr = result.sendResult || {};
          // Refresh the FACTU version list (a new immutable version was added).
          const fp = await getFactuPdf(viewInv.id);
          if (fp && fp.ok) setFactuPdf({ versions: fp.versions || [], latestWebUrl: fp.latestWebUrl || null });
          setSelVersion("");
          /* 213B §13 — ONE toast (212 [METHOD] (a)): marked sent as before; when Graph refused the draft
             (`draftCreated: false`, the server's best-effort), the toast says so, with Graph's sentence —
             the old toast said « draft created » either way. */
          const draft = draftOutcomeOf(sr);
          if (!draft.created) {
            sayPf(t("proforma.toast.draft_failed", { error: draft.error, fallback: `Marked sent and saved in 04. FACTU, but the Outlook draft could not be created: ${draft.error}. Regenerate makes a new one.` }), "warning");
          } else {
            toast(t("proforma.sent_to_client_toast", { fallback: "Sent to client — new PDF version saved to 04. FACTU, draft created, marked Sent." }));
          }
          landTab(tab, draft.link);
        } else {
          landTab(tab, null);
          toast(
            `⚠ ${result.error || t("proforma.toast.send_client_failed", { fallback: "Send to client failed." })}`,
            "error",
            // 209 §5 [RULED 18] — neither the client nor its case has a language: the case form, one click away
            { action: languageRefusalAction(result.body, t("case.language.open_form", { fallback: "Open the case form" })) },
          );
        }
      } catch (e) {
        // 213B §13 — a thrown call closes the tab and says the existing words (there was no catch)
        landTab(tab, null);
        console.warn("[proforma] send to client threw:", e);
        sayPf(`⚠ ${t("proforma.toast.send_client_failed", { fallback: "Send to client failed." })}`, "error");
      } finally {
        setSending(false);
      }
    };

    // Phase 40 (D9) — "Generate PDF": PURE PREVIEW. Server-side generate
    // (merge → Graph PDF) and open the real PDF; does NOT save to FACTU (only
    // "Send to client" saves), so it never flips factuPdf.exists. Prod-only.
    const handleGeneratePdf = async () => {
      if (genBusy) return;
      setGenBusy(true);
      const tab = openTabNow(); // 213B §12 — at the click
      try {
        const r = await generateProForma(viewInv.id, {});
        if (r.ok) {
          const url = r.pdfBase64 ? pdfBlobUrl(String(r.pdfBase64)) : null;
          if (url) {
            if (previewPdf) URL.revokeObjectURL(previewPdf.url);
            setPreviewPdf({ url });
          }
          landTab(tab, url);
          setXmlRecon(r.xml && r.xml.present ? r.xml : null);
          toast(t("proforma.pdf_generated", { fallback: "Pro forma preview generated." }));
        } else {
          landTab(tab, null);
          toast(
            `⚠ ${r.error || t("proforma.toast.pdf_failed", { fallback: "Could not generate the PDF." })}`,
            "error",
            // 209 §5 [RULED 18] — neither the client nor its case has a language: the case form, one click away
            { action: languageRefusalAction(r.body, t("case.language.open_form", { fallback: "Open the case form" })) },
          );
        }
      } catch (e) {
        // 213B §12 — a thrown call closes the tab and says `pdf_failed` (there was no catch)
        landTab(tab, null);
        console.warn("[proforma] generate threw:", e);
        sayPf(`⚠ ${t("proforma.toast.pdf_failed", { fallback: "Could not generate the PDF." })}`, "error");
      } finally {
        setGenBusy(false);
      }
    };
    // 213B §12 — « Afficher le PDF » on the generated preview: the same blob, no second round trip, at the click
    const handleShowPreview = () => {
      if (previewPdf) window.open(previewPdf.url, "_blank");
    };

    // Phase 40 (D9-REVISED) — "Show PDF": open the selected FACTU version (or the
    // latest). Each send produced an immutable version.
    const handleShowPdf = () => {
      const url = selVersion || factuPdf.latestWebUrl;
      if (url) window.open(url, "_blank");
    };

    // 179 C.3 — `handleUblDownload` STOOD HERE.

    // Gate 4 C2 §3.4/§3.5 — generic transition helper for the matrix in
    // src/lib/invoiceTransitions.js. Goes through PUT /:id/status so the
    // server matrix gates the write and the structured 409 envelope
    // surfaces user-facing reasons.
    // Phase 35 Stage-3 — Cancel a pro forma. Dedicated route flips it to
    // cancelled AND releases its time entries to the billable pool (bypasses the
    // generic-PUT edit lock). It stays in the Live list with the cancelled title.
    const handleCancel = async () => {
      if (transitioning) return;
      askConfirm({
        // 161B §5 — THE DIALOG SAID "Delete undefined?". `ConfirmDeleteDialog`
        // falls back to `Delete ${label}` when no `title` is passed
        // (useConfirmAndUndo.tsx:230-238), and this call passed neither — so
        // the heading over a CANCEL question named a delete, of nothing.
        // The document's own number is what a confirmation should be headed
        // by, and `confirmLabel` says which of the two "cancel"s the
        // affirmative button means.
        title: t("proforma.cancel_title", {
          number: viewInv.number,
          fallback: `Cancel ${viewInv.number}?`,
        }),
        confirmLabel: t("proforma.cancel_confirm_btn", {
          fallback: "Cancel pro forma",
        }),
        message: t("proforma.cancel_confirm", {
          fallback:
            "Cancel this pro forma invoice? Its time entries return to the billable pool. You can restore it later.",
        }),
        onConfirm: async () => {
          setTransitioning(true);
          try {
            const result = await cancelProForma(viewInv.id);
            if (result.ok) {
              await refetchInvoice();
              if (sInv && result.invoice)
                sInv((arr: any) =>
                  (arr || []).map((i: any) => (i.id === viewInv.id ? result.invoice : i)),
                );
              toast(
                t("proforma.cancelled_toast", {
                  fallback: "Pro forma invoice cancelled — entries released.",
                }),
              );
              // 157 §3 — CANCELLING ENDS. Without this the modal stayed open
              // and re-rendered into its cancelled variant (isCancelled), so
              // the user was handed the un-cancel footer the instant they
              // confirmed a cancel and read it as "something more is wanted".
              // Nothing is lost by closing: the list behind updates through
              // sInv above. The FAILURE path below deliberately stays open,
              // because there the user does have something left to do.
              onClose && onClose();
            } else {
              toast(
                `⚠ ${result.error || t("proforma.toast.cancel_failed", { fallback: "Cancel failed." })}`,
                "error",
              );
            }
          } finally {
            setTransitioning(false);
          }
        },
      });
    };

    // Phase 35 Stage-3 — Restore a cancelled pro forma: re-binds its snapshot
    // entries; any re-billed elsewhere are skipped (not blocking) and reported.
    // 179 C.1b [RULED 2026-09-10] — THE REVIVE IS HONEST ABOUT ITS TOTAL: the
    // dry run is asked first, and when entries are now on other documents or
    // the total moves, the confirm says so in numbers ("N of M entries are now
    // on {numbers}. Total {old} → {new}. Restore with the remaining entries?");
    // otherwise the restore runs as it always did. The real call re-binds the
    // free set only, rewrites `entries` and recomputes the ladder (server).
    const handleRestore = async () => {
      if (transitioning) return;
      setTransitioning(true);
      let partial: null | { n: number; m: number; numbers: string; old: number | null; next: number | null } = null;
      try {
        const plan = await uncancelProForma(approveTargetId, { dryRun: true });
        if (!plan.ok) {
          toast(`⚠ ${plan.error || t("proforma.toast.restore_failed", { fallback: "Restore failed." })}`, "error");
          return;
        }
        const body = (plan.invoice || {}) as { rebound?: unknown[]; skipped?: { id: number; onInvoice: string | null }[]; missing?: unknown[]; oldTotal?: number | null; newTotal?: number | null };
        const skippedRows = Array.isArray(body.skipped) ? body.skipped : [];
        const oldT = body.oldTotal ?? null;
        const newT = body.newTotal ?? null;
        if (skippedRows.length > 0 || oldT !== newT) {
          const m = (Array.isArray(body.rebound) ? body.rebound.length : 0) + skippedRows.length + (Array.isArray(body.missing) ? body.missing.length : 0);
          const numbers = [...new Set(skippedRows.map((s) => s.onInvoice).filter(Boolean))].join(", ") || "—";
          partial = { n: skippedRows.length, m, numbers, old: oldT, next: newT };
        }
      } finally {
        setTransitioning(false);
      }
      if (partial) {
        askConfirm({
          title: t("proforma.cancelled_restore", { fallback: "Restore" }),
          message: t("proforma.restore_partial_confirm", withCount(partial.n, "n", { m: partial.m,
            numbers: partial.numbers,
            old: fc(partial.old ?? 0, viewInv.currency),
            new: fc(partial.next ?? 0, viewInv.currency),
            fallback: `${partial.n} of ${partial.m} entries are now on ${partial.numbers}. Total ${fc(partial.old ?? 0, viewInv.currency)} → ${fc(partial.next ?? 0, viewInv.currency)}. Restore with the remaining entries?`,
          })),
          confirmLabel: t("proforma.cancelled_restore", { fallback: "Restore" }),
          onConfirm: () => { void doRestore(); },
        });
        return;
      }
      await doRestore();
    };
    // A declaration, so the caller above may name it before this line.
    async function doRestore() {
      if (transitioning) return;
      setTransitioning(true);
      try {
        const result = await uncancelProForma(viewInv.id);
        if (result.ok) {
          await refetchInvoice();
          if (sInv && result.invoice)
            sInv((arr: any) =>
              (arr || []).map((i: any) => (i.id === viewInv.id ? result.invoice : i)),
            );
          const skipped = result.skipped || [];
          toast(
            skipped.length
              ? t("proforma.restored_partial", {
                  count: skipped.length,
                  fallback: `Pro forma invoice restored — ${skipped.length} entr${skipped.length === 1 ? "y" : "ies"} skipped (re-billed elsewhere).`,
                })
              : t("proforma.restored_toast", {
                  fallback: "Pro forma invoice restored — entries re-bound.",
                }),
          );
        } else {
          toast(
            `⚠ ${result.error || t("proforma.toast.restore_failed", { fallback: "Restore failed." })}`,
            "error",
          );
        }
      } finally {
        setTransitioning(false);
      }
    }

    // 179 B.7 [RULED, rulings 4 and 5] — THE APPROVAL ACT from the panel:
    // POST /:id/approve, then refetch (the pill goes purple, the sends
    // return). No dialog: the brief names the act as one click, and the
    // server refuses on its own if the click was not the approver's.
    const handleApprove = async () => {
      if (approving) return;
      setApproving(true);
      try {
        const result = await approveProForma(approveTargetId);
        if (result.ok) {
          await refetchInvoice();
          if (sInv && result.invoice)
            sInv((arr: any) =>
              (arr || []).map((i: any) => (i.id === approveTargetId ? result.invoice : i)),
            );
          toast(t("proforma.approved_toast", { fallback: "Pro forma invoice approved." }));
        } else {
          toast(
            `⚠ ${result.error || t("proforma.toast.approve_failed", { fallback: "Approval failed." })}`,
            "error",
          );
        }
      } finally {
        setApproving(false);
      }
    };

    // Phase 35 Stage-3 r2 (§2) — Delete = PERMANENT hard delete (DELETE /:id is
    // now a hard delete server-side). Allowed only while un-sent; the server
    // refuses once sent. Entries are released to the billable pool.
    const handleTrash = async () => {
      if (trashing) return;
      askConfirm({
        // 161C §3 — this is the one the 161B fallback was named after
        // ("Delete undefined?"); now it says which document.
        title: t("confirm.delete_proforma_title", {
          number: viewInv.number,
          fallback: `Delete ${viewInv.number}?`,
        }),
        message: t("proforma.delete_confirm", {
          fallback:
            "Delete this pro forma invoice permanently? A deleted pro forma invoice cannot be restored.",
        }),
        confirmLabel: t("common.delete", { fallback: "Delete" }),
        onConfirm: async () => {
          setTrashing(true);
          try {
            const result = await softDeleteInvoice(viewInv.id);
            if (result.ok) {
              if (sInv) sInv((arr: any) => (arr || []).filter((i: any) => i.id !== viewInv.id));
              toast(
                t("proforma.deleted_toast", { fallback: "Pro forma invoice deleted." }),
              );
              onClose && onClose();
            } else {
              toast(
                `⚠ ${result.error || t("proforma.toast.delete_failed", { fallback: "Delete failed." })}`,
                "error",
              );
              console.error("[ProFormaModal] delete failed", result);
            }
          } finally {
            setTrashing(false);
          }
        },
      });
    };

    // Phase M+3.5 D4 — Restore from trash (modal-side). Mirrors the
    // inline row-level Restore in InvoiceView. The optional onRestored
    // callback lets the parent (App.jsx) refresh both the live `inv`
    // list and the InvoiceView trash bucket.
    const handleRestoreFromTrash = async () => {
      if (restoring) return;
      setRestoring(true);
      try {
        const result = await postRestoreInvoice(viewInv.id);
        if (result.ok) {
          toast(t("invoice.toast.proforma_restored", { number: viewInv.number || "" }));
          if (onRestored) onRestored(result.invoice || { id: viewInv.id });
          onClose && onClose();
        } else {
          const msg = trashReasonToMessage(result.reason);
          toast(
            msg ||
              `⚠ ${result.error || t("proforma.toast.restore_failed", { fallback: "Restore failed." })}`,
            "error",
          );
          console.error("[ProFormaModal] restore failed", result);
        }
      } finally {
        setRestoring(false);
      }
    };

    // Phase 35 Stage-3 — the C2 "Send to client" flow (PDF → Graph draft +
    // archive + lock) is removed per the correction brief (item 2). Pro formas
    // are created active; lifecycle is Cancel/Restore + Trash only.

    // Gate 4 C4 §3.6 — Trash button predicate. Two states:
    //   - canDeletePerm AND !sent → visible + active
    //   - canDeletePerm AND  sent → visible + disabled with tooltip
    //   - !canDeletePerm          → hidden entirely (the server refuses with
    //                               403; the FE mirrors visibility so it does
    //                               not surface an action the user cannot use).
    // 179 C.7 — PARITY WITH THE SERVER. The comment that stood here said this
    // predicate "mirrors the server's rp-or-admin-only gate at the DELETE
    // handler"; that gate has not existed since Phase 35 Stage-3 r2 §6 — the
    // server's DELETE is gated on the DELEGATE right (`gateProforma(…,
    // 'unsent')` → isProformaDelegate: admin · RP of the matter · a LIVE allow
    // grant). The client read only the RP claim and isAdmin, so a granted
    // colleague never saw the button the server would have honoured. Aligned
    // to the same three: the grant arm is `clientCan` on the key (it resolves
    // the per-case grant; the key has no role default, so an RP is NOT a
    // `clientCan` holder — which is why the RP claim stays beside it, as in
    // usePermissions' clientIsBillingDelegate mirror for markBilled).
    const isAdminViewer = !!viewer?.isAdmin;
    const isRpForCase = !!(
      (viewInv.caseId != null &&
        Array.isArray(viewer?.myRpCaseIds) &&
        viewer.myRpCaseIds.includes(viewInv.caseId)) ||
      (viewInv.ccId != null &&
        Array.isArray(viewer?.myRpCcIds) &&
        viewer.myRpCcIds.includes(viewInv.ccId))
    );
    const canDeletePerm =
      viewerCanInvoices &&
      (isAdminViewer ||
        isRpForCase ||
        clientCan("caseView.proformas.unsent", { caseId: viewInv.caseId, ccId: viewInv.ccId }));
    // Phase 35 Stage-3 r2 — "sent" = either send channel (or legacy Billit push).
    const isSent = !!(viewInv.sentToClientAt || viewInv.sentToPeppolAt || viewInv.billitSubmittedAt);
    // §2 — Delete is permanent and refused once sent (dimmed + tooltip).
    const deleteAllowed = canDeletePerm && !isSent;
    const deleteTooltip = isSent
      ? t("proforma.delete_blocked_sent", { fallback: "Deleting not allowed when the Pro Forma invoice has been sent" })
      : trashing
        ? t("proforma.deleting", { fallback: "Deleting…" })
        : t("proforma.delete_tip", { fallback: "Delete this pro forma invoice permanently" });
    // 157 §4 (iv) — the gate is `canDeletePerm` (does the button exist at all)
    // and `deleteAllowed` (is it live). 157A's follow-up removed the per-footer
    // label this was briefly a factory for, so it is one constant again: BOTH
    // footers show the same word for the same act.
    const trashBtn =
      canDeletePerm ? (
        <button
          key="delete"
          type="button"
          className="btn btn-danger"
          disabled={!deleteAllowed || trashing}
          onClick={handleTrash}
          title={deleteTooltip}
          style={{
            flex: 1,
            padding: "8px 10px",
            fontSize: FS.s11,
            opacity: !deleteAllowed || trashing ? 0.5 : 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 5,
          }}
        >
          <Trash2 size={13} /> {t("proforma.delete", { fallback: "Delete" })}
        </button>
      ) : null;

    // Footer buttons.
    // Gate 4 C2 §3.11 — PDF preview is always available in view mode now,
    // including from `draft` (was hidden on draft pre-C2).
    const footerButtons = [];
    // Correction 3 (Item 1) — standard, equal-height footer buttons (no ad-hoc
    // colour tints). rowBtn drives height/flex; class "bt"/"bt brd" provides the
    // app-standard look.
    const rowBtn = {
      flex: 1,
      padding: "8px 10px",
      fontSize: FS.s11,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 5,
    };
    // Correction 4 (C1.1) — restore the house per-button pastel tints (pulled
    // from the pre-Correction-3 footer) onto the three equal-height rows.
    const pdfTint = { ...rowBtn, background: "rgba(80,90,110,.15)", color: "var(--ts)", border: "1px solid var(--br)" };
    const greenTint = { ...rowBtn, background: "rgba(52,211,153,.14)", color: "var(--gn)", border: "1px solid rgba(52,211,153,.4)" };
    const amberTint = { ...rowBtn, background: "rgba(245,158,11,.18)", color: "var(--am)", border: "1px solid rgba(245,158,11,.35)" };
    // 179 B.7 — the purple family the Restore button already wears: the
    // colour of the approval act (ruling 1). `flex: 2` spans both send slots.
    const purpleTint = { ...rowBtn, flex: 2, background: "rgba(167,139,250,.15)", color: "var(--pp)", border: "1px solid rgba(167,139,250,.3)" };
    const hasVersions = !!(factuPdf.versions && factuPdf.versions.length);
    const showUrl = selVersion || factuPdf.latestWebUrl;
    // Row 2 first slot — Generate (preview) before any send; the version picker +
    // Show PDF + Regenerate (re-send) once FACTU versions exist (Correction 2).
    const pdfControl = hasVersions ? (
      <>
        <MenuSelect
          className="field-input"
          value={showUrl || ""}
          onChange={(u) => setSelVersion(u)}
          title={t("proforma.factu_versions", { fallback: "Sent versions saved in 04. FACTU" })}
          style={{ width: 64, padding: "8px 6px", fontSize: FS.s11 }}
          options={factuPdf.versions.map(
            (v: any) => [String(v.webUrl || ""), `v${v.version}`] as const,
          )}
        />
        <button type="button" className="btn" style={pdfTint} onClick={handleShowPdf} disabled={!showUrl}>
          <Download size={13} /> {t("proforma.show_pdf", { fallback: "Show PDF" })}
        </button>
        <button
          type="button"
          className="btn"
          style={{ ...pdfTint, opacity: sending ? 0.6 : 1 }}
          onClick={handleSendToClient}
          disabled={sending}
          title={t("proforma.regenerate_hint", { fallback: "Regenerate — re-sends: saves a NEW version to 04. FACTU and creates a fresh draft" })}
        >
          {t("proforma.regenerate_pdf", { fallback: "Regenerate" })}
        </button>
      </>
    ) : previewPdf ? (
      /* 213B §12 — once generated, the slot reads « Afficher le PDF » for the rest of the window's life (the same
         preview, no second call); a sent version takes the slot as above */
      <button type="button" className="btn" style={pdfTint} onClick={handleShowPreview} data-pf-show-preview="">
        <Download size={13} /> {t("proforma.show_pdf", { fallback: "Show PDF" })}
      </button>
    ) : (
      <button
        type="button"
        className="btn"
        style={{ ...pdfTint, opacity: genBusy ? 0.6 : 1 }}
        onClick={handleGeneratePdf}
        disabled={genBusy}
      >
        <Download size={13} /> {genBusy
          ? t("proforma.generating_pdf", { fallback: "Generating…" })
          : t("proforma.generate_pdf", { fallback: "Generate PDF" })}
      </button>
    );

    // Phase M+3.5 D4 — trashed (soft-deleted) preview footer. Close +
    // Restore only; no transitions, no PDF, no trash (already trashed).
    // The Restore button is gated on lateral.invoices like the inline
    // row-level Restore in InvoiceView.
    if (isTrashedView) {
      footerButtons.push(
        <div key="trashed" className="modal-footer">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 6,
            }}
          >
            <CancelButton
              label={t("common.close", { fallback: "Close" })}
              onClick={onClose}
            />
            {viewerCanInvoices && (
              <SaveButton
                label={
                  restoring
                    ? t("proforma.restoring", { fallback: "Restoring…" })
                    : t("proforma.restore_proforma", {
                        fallback: "Restore pro forma invoice",
                      })
                }
                icon={null}
                onClick={handleRestoreFromTrash}
                disabled={restoring}
                title={
                  restoring
                    ? t("proforma.restoration_in_progress", {
                        fallback: "Restoration en cours…",
                      })
                    : t("proforma.restore_clear_deleted_at", {
                        fallback:
                          "Restaurer cette pro forma (efface deletedAt).",
                      })
                }
                style={{
                  padding: "10px 16px",
                  fontSize: FS.s12,
                  opacity: restoring ? 0.6 : 1,
                  background: "rgba(167,139,250,.15)",
                  color: "var(--pp)",
                  border: "1px solid rgba(167,139,250,.3)",
                }}
              />
            )}
          </div>
        </div>,
      );
    } else if (isCancelled) {
      // 157 §4 — Cancelled: Restore · Delete. NO PDF control.
      //
      // (i) `pdfControl` rendered an ACTIVE "Générer le PDF" whose only
      //     outcome here was the error toast "Impossible de générer une pro
      //     forma annulée". A button whose only outcome is an error is not a
      //     button. Removed from THIS footer only — the active and trashed
      //     footers keep theirs.
      // (ii) The two remaining actions sit in one row, each flex: 1. Close
      //     stays where it was.
      // (iii) Renamed. "Undo cancelling" is a double negative the reader has
      //     to unwind. NEW keys, not edited ones: the strings seed is
      //     createMany + skipDuplicates, so changing an existing key's text
      //     would seed nothing and the old words would still render.
      //     `proforma.undo_cancel` is deliberately NOT collapsed into
      //     `proforma.restore_proforma` — that one belongs to the TRASHED
      //     footer and means "clear deletedAt". Different acts, different
      //     states, and one day different words.
      //     THE DELETE LABEL WAS REVERTED (157A follow-up, ruled): it reads
      //     the shared `proforma.delete` — Supprimer / Verwijderen / Delete —
      //     because "Effacer" already means "clear this field" in this app
      //     (the description-prefix clear button in TimeEntryModal), while
      //     "Supprimer" is the app-wide delete. Only RESTORE was renamed.
      // 157 §4 FOOTER, RULED (2026-08-31) — THE SHAPE IS THE EDIT MODAL'S.
      // Close stood FIRST here, in a space-between row of its own, with the
      // two actions beneath it — the reverse of every other footer this
      // component renders. The active ("Edit Pro forma invoice") footer ends
      // with Close alone and CENTRED on the bottom row, under its action
      // rows, and the cancelled footer is the same modal in another state, so
      // it reads the same way now: Restore + Delete share the row above at
      // half width each, Close closes underneath.
      // Restore also moves onto `rowBtn`, the same constant the active
      // footer's lifecycle row uses and the one `trashBtn` was already built
      // from (flex 1 · 8px 10px · FS.s11). It carried 10px 16px / FS.s12,
      // so the pair it is meant to halve the row with did not match it.
      footerButtons.push(
        <div key="cancelled" className="modal-footer">
          {(viewerCanInvoices || trashBtn) && (
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              {/* RULED 2026-09-01 — RÉTABLIR WAS INVISIBLE. `btn-ghost` +
                  bare `rowBtn` gave it no fill and no border of its own, so
                  the one constructive action in this footer read as absent
                  beside a red Supprimer. It takes `greenTint` — the exact
                  treatment "Envoyer au client" carries in the edit footer,
                  same token family, same `opacity` handling — and since
                  `greenTint` already spreads `rowBtn`, the half-width row
                  geometry is unchanged. Supprimer keeps its red. */}
              {viewerCanInvoices && (
                <button
                  type="button"
                  className="btn"
                  disabled={transitioning}
                  title={t("proforma.cancelled_restore_tip", { fallback: "Restore — re-activate this pro forma and re-bind its entries." })}
                  style={{ ...greenTint, opacity: transitioning ? 0.6 : 1 }}
                  onClick={handleRestore}
                >
                  <RotateCcw size={13} /> {t("proforma.cancelled_restore", { fallback: "Restore" })}
                </button>
              )}
              {trashBtn}
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <CancelButton label={t("common.close", { fallback: "Close" })} onClick={onClose} />
          </div>
        </div>,
      );
    } else {
      // §2 / Phase 35 Stage-3 r2 (§2) — Active (non-cancelled). The two send
      // channels are INDEPENDENT: each Send button renders while its OWN flag is
      // still null, so sending one never strands the other. Edit + Delete show
      // only while BOTH channels are unsent (the sent-lock tier `locked`).
      // Cancel and Download (top row) are always available.
      footerButtons.push(
        <div key="active" className="modal-footer">
          {/* 169 G2/G4 [RULED 2026-09-06] — THE LANGUAGE SELECT IS GONE. It was
              a per-generation override on the PDF, and G4 rules that a
              document's language is the CLIENT's, set in the client record —
              so an override at the moment of printing is a second, contrary
              answer to a question the client already answers. Both send calls
              now pass no override and `pickDocumentLanguage` (docGen.js:629)
              resolves the language from `Client.language`. Row 2 becomes the
              first row. */}
          {/* Row 2 — produce / send group (equal-height standard buttons). */}
          <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
            {pdfControl}
            {/* 179 B.7 [RULED, ruling 4] — TWO SHAPES. Not approved: ONE
                double-width purple Approve where the two send buttons stand —
                live for a holder of the approve right, dimmed for everyone else
                with the hover naming what is needed. Approved: the two send
                buttons. The server refuses on its own either way (B.3/B.4); this
                is the courtesy. (209 §14 [RULED 19] — for every firm.) */}
            {sentUnapproved && (
              <span
                title={t("proforma.sent_unapproved_tip", { fallback: "This pro forma went out before it was approved. Approval must come before sending, so it can no longer be approved." })}
                style={{ ...rowBtn, flex: 2, background: "rgba(120,120,120,.10)", color: "var(--tm)", border: "1px solid var(--br)", borderRadius: RAD.r6, fontWeight: 600, cursor: "default" }}
              >
                {t("invoice.approval.sent_unapproved", { fallback: "Sent without approval" })}
              </span>
            )}
            {!sentUnapproved && (!isApproved ? (
              <button
                type="button"
                className="btn"
                disabled={approving || !canApprove}
                title={canApprove
                  ? t("proforma.approve_tip", { fallback: "Approve this pro forma invoice — it can then be sent to the client or to Peppol." })
                  : t("proforma.approve_needed_tip", { fallback: "Needs approval of the Responsible Partner before sending to client or to Peppol" })}
                style={{ ...purpleTint, opacity: approving || !canApprove ? 0.5 : 1 }}
                onClick={() => { void handleApprove(); }}
              >
                {t("proforma.approve", { fallback: "Approve" })}
              </button>
            ) : (
              <>
                {!viewInv.sentToClientAt && (
                  <button
                    type="button"
                    className="btn"
                    disabled={sending}
                    title={t("proforma.send_to_client_tip", { fallback: "Generate the PDF, save it to the client 04. FACTU folder, draft the email, and mark To Client = Sent." })}
                    style={{ ...greenTint, opacity: sending ? 0.6 : 1 }}
                    onClick={handleSendToClient}
                  >
                    {t("proforma.send_to_client", { fallback: "Send to client" })}
                  </button>
                )}
                {!viewInv.sentToPeppolAt && (
                  <button
                    type="button"
                    className="btn"
                    disabled={pushing}
                    title={t("proforma.send_to_peppol_tip", { fallback: "Export the Peppol XML and mark To Peppol = Sent." })}
                    style={{ ...amberTint, opacity: pushing ? 0.6 : 1 }}
                    onClick={handlePushToBillit}
                  >
                    {t("proforma.send_to_peppol", { fallback: "Send to Peppol" })}
                  </button>
                )}
              </>
            ))}
          </div>
          {/* Row 3 — lifecycle group.
              179 B.7 [RULED, ruling 5] — approval is the freeze: the document
              approved, Edit, Cancel and Delete render only for a holder of the
              approve right (the server refuses everyone else with
              invoice.approved_locked, B.5). `viewerCanInvoices` stays the outer
              condition. (209 §14 [RULED 19] — for every firm.) */}
          {viewerCanInvoices && (!isApproved || canApprove) && (
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              {!locked && onRemake && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  title={t("proforma.edit_tip", { fallback: "Edit this pro forma invoice (same number + entries)." })}
                  style={rowBtn}
                  onClick={() => onRemake({
                    editId: viewInv.id,
                    entryIds: Array.isArray(viewInv.entries) ? viewInv.entries : [],
                    csId: viewInv.caseId || viewInv.ccId,
                    csType: viewInv.caseId ? "ca" : "cc",
                    remakeNumber: viewInv.number,
                    // 169 G3 — THE KIND TRAVELS. Without it a retainer opened the
                    // ACTIVITIES form: no amount field, a period selector and an
                    // entries annex on a document that has none. The server was
                    // defensive (`putKind` comes off the row, invoices.js:826) so
                    // the stored kind survived — the FORM was the lie.
                    docKind: viewInv.docKind === "retainer" ? "retainer" : "activities",
                    // 169 G3 — and the DOCUMENT'S OWN VALUES, so the form that
                    // opens is this document rather than a fresh one shaped like
                    // the case. An explicit read-set, like every other wire shape
                    // in this tree: what is not named here is not prefilled.
                    doc: {
                      currency: viewInv.currency,
                      costsRatio: viewInv.costsRatio,
                      vatRate: viewInv.vatRate,
                      commercialDeduction: viewInv.commercialDeduction,
                      retainerDeduction: viewInv.retainerDeduction,
                      subtotalFees: viewInv.subtotalFees,
                      issueDate: viewInv.issueDate,
                      dueDate: viewInv.dueDate,
                      periodMode: viewInv.periodMode,
                      periodFrom: viewInv.periodFrom,
                      periodTo: viewInv.periodTo,
                      bankAccountIdx: viewInv.bankAccountIdx,
                      paymentConditions: viewInv.paymentConditions,
                      notes: viewInv.notes,
                      // 171 §1e — WHAT THIS DOCUMENT CURRENTLY DRAWS, so the
                      // edit's source picker opens on the ticks the document
                      // actually holds instead of guessing them. It rides the
                      // same read-set as everything else here: this arm has
                      // already fetched through GET /:id, which is the ONLY
                      // read that serves `retainerSources` (the list serves
                      // null), so there is nothing to fetch a second time.
                      // `id` joined the served row in §1e for exactly this —
                      // the tick map is keyed by the source invoice's id.
                      retainerSources: viewInv.retainerSources,
                    },
                  })}
                >
                  <Pencil size={13} /> {t("proforma.edit", { fallback: "Edit" })}
                </button>
              )}
              <button
                type="button"
                className="btn btn-danger"
                disabled={transitioning}
                title={t("proforma.cancel_proforma_tip", { fallback: "Cancel this pro forma invoice — its time entries return to the billable pool." })}
                style={{ ...rowBtn, opacity: transitioning ? 0.6 : 1 }}
                onClick={handleCancel}
              >
                <Ban size={13} /> {t("proforma.cancel", { fallback: "Cancel" })}
              </button>
              {!locked && trashBtn}
            </div>
          )}
          {/* Close */}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <CancelButton label={t("common.close", { fallback: "Close" })} onClick={onClose} />
          </div>
        </div>,
      );
    }

    const headerLabel = getInvoiceHeaderLabel(viewInv);
    // 169 G2 [RULED 2026-09-06] — THE HEADER IS THE DOCUMENT'S OWN NAME. It
    // read "Edit Pro forma invoice — PF-2026/031" on a screen that edits
    // nothing, which is what made the arm read as an editor. The two INERT
    // states keep their word, because "cancelled" and "trash" are facts about
    // the document a reader must not have to infer from a greyed button.
    const editModalTitle = isTrashedView
      ? t("proforma.title_trashed", { fallback: "Pro forma invoice (trash)" })
      : isCancelled
        ? t("proforma.title_cancelled", { fallback: "Cancelled pro forma invoice" })
        : null;

    return (
      <ModalLayout
        title={editModalTitle
          ? `${editModalTitle} — ${viewInv.number}`
          : t("proforma.title_view", { fallback: "Pro forma {number}" }).replace("{number}", String(viewInv.number || ""))}
        onClose={onClose}
        maxWidth={680}
        footer={footerButtons}
      >
        {/* Phase 40 (C2.4) — preview reconciliation: PDF data vs the filled Peppol
            XML, after "Generate PDF". Well-formed + matches-PDF + empty-marker
            flags ONLY (NOT a Peppol-validity check). Pure preview — writes nothing. */}
        {xmlRecon && xmlRecon.present && (
          <div style={{ border: "1px solid var(--br)", borderRadius: RAD.r6, padding: 10, marginBottom: 12, fontSize: FS.s11 }}>
            <div style={{ fontWeight: 600, marginBottom: 4, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {t("proforma.xml_recon_title", { fallback: "Peppol XML — preview reconciliation" })}
              <span style={{ fontSize: FS.s9_5, fontWeight: 500, color: xmlRecon.wellFormed ? "var(--gn)" : "var(--rd)" }}>
                {xmlRecon.wellFormed
                  ? t("proforma.xml_wellformed", { fallback: "✓ well-formed" })
                  : `✗ ${t("proforma.xml_not_wellformed", { fallback: "not well-formed" })}: ${xmlRecon.error || ""}`}
              </span>
            </div>
            <p style={{ fontSize: FS.s9_5, color: "var(--tm)", margin: "0 0 6px" }}>
              {t("proforma.xml_recon_scope", { fallback: "Well-formed + matches the PDF + no empty markers — NOT a Peppol-validity check." })}
            </p>
            <Table style={{ width: "100%", borderCollapse: "collapse", fontSize: FS.s10_5 }}>
              <thead>
                <tr style={{ color: "var(--tm)", textAlign: "left" }}>
                  <th style={{ padding: "2px 6px" }}>{t("proforma.xml_field", { fallback: "Field" })}</th>
                  <th style={{ padding: "2px 6px" }}>{t("proforma.xml_pdf", { fallback: "PDF" })}</th>
                  <th style={{ padding: "2px 6px" }}>{t("proforma.xml_xml", { fallback: "XML" })}</th>
                  <th style={{ padding: "2px 6px", textAlign: "center" }}>=</th>
                </tr>
              </thead>
              <tbody>
                {(xmlRecon.fields || []).map((f: any) => (
                  <tr key={f.key} style={{ borderTop: "1px solid var(--br)" }}>
                    <td style={{ padding: "2px 6px" }}>{f.label}</td>
                    <td style={{ padding: "2px 6px", fontFamily: "'JetBrains Mono',monospace" }}>{f.pdfValue || "—"}</td>
                    <td style={{ padding: "2px 6px", fontFamily: "'JetBrains Mono',monospace", color: f.match ? "var(--tp)" : "var(--rd)" }}>{f.xmlValue || "—"}</td>
                    <td style={{ padding: "2px 6px", textAlign: "center", fontWeight: 700, color: f.match ? "var(--gn)" : "var(--rd)" }}>{f.match ? "✓" : "✗"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {xmlRecon.emptyMarkers && xmlRecon.emptyMarkers.length > 0 && (
              <p style={{ fontSize: FS.s10, color: "var(--am)", margin: "6px 0 0" }}>
                ⚠ {t("proforma.xml_empty_markers", { fallback: "Markers resolved empty" })}: {xmlRecon.emptyMarkers.join(", ")}
              </p>
            )}
          </div>
        )}
        {/* Phase M+3.5 D4 — trashed-invoice banner. Distinct red-tinted
          background to set this preview apart from live editing modes.
          The footer below is reduced to Close + Restore. */}
        {isTrashedView && (
          <div
            style={{
              marginBottom: 12,
              padding: "8px 12px",
              background: "rgba(248,113,113,.10)",
              border: "1px solid rgba(248,113,113,.35)",
              borderRadius: RAD.r6,
              fontSize: FS.s11,
              color: "var(--rd)",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Trash2 size={12} />
            <span>
              {t("proforma.trashed_restore", {
                fallback: "Trashed invoice — restore to edit",
              })}
            </span>
          </div>
        )}
        {/* 179 C.9b — the revision badge STOOD HERE: `revision` was always 1 (the
            create sent it) so `> 1` never fired, and `rejectionComment` was never
            a column at all. Both dropped with the ghost columns. */}
        <div
          style={{
            border: "1px solid var(--br)",
            borderRadius: RAD.r10,
            padding: 20,
            background: "rgba(255,255,255,.02)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 16,
              gap: 16,
            }}
          >
            <div>
              <div style={{ fontSize: FS.s11, fontWeight: 600 }}>
                {viewInv.clientName}
              </div>
              <div
                style={{ fontSize: FS.s10, color: "var(--tm)", lineHeight: 1.5 }}
              >
                {viewInv.clientAddress}
              </div>
              {viewInv.clientVAT && (
                <div style={{ fontSize: FS.s10, color: "var(--tm)" }}>
                  {t("proforma.vat_label", { fallback: "VAT" })}:{" "}
                  {viewInv.clientVAT}
                </div>
              )}
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{ fontSize: FS.s13, fontWeight: 700, color: "var(--ac)" }}
              >
                {headerLabel}
              </div>
              <div
                style={{
                  fontSize: FS.s11,
                  fontFamily: "'JetBrains Mono',monospace",
                  color: "var(--ts)",
                }}
              >
                {viewInv.number}
              </div>
              <div style={{ fontSize: FS.s10, color: "var(--tm)", marginTop: 4 }}>
                {t("common.date", { fallback: "Date" })}:{" "}
                {fd(viewInv.invoiceDate)}
              </div>
              <div style={{ fontSize: FS.s10, color: "var(--tm)" }}>
                {t("proforma.due", { fallback: "Due" })}: {fd(viewInv.dueDate)}
              </div>
              {/* 161C §1 — no Period line on a retainer or a credit. The
                  `periodDisplay &&` guard already hid it for documents 161 §4
                  created (it never writes the field), but a row saved before
                  that — or by any other path — would still print one, and the
                  rule is the document kind, not whether a string happens to
                  be there. */}
              {viewInv.periodDisplay &&
                (!viewInv.docKind || viewInv.docKind === "activities") && (
                <div style={{ fontSize: FS.s10, color: "var(--ts)", marginTop: 4 }}>
                  {t("proforma.period", { fallback: "Period" })}:{" "}
                  {viewInv.periodDisplay}
                </div>
              )}
              <div
                style={{
                  fontSize: FS.s10,
                  color: "var(--tm)",
                  marginTop: 4,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  justifyContent: "flex-end",
                }}
              >
                <span>
                  {t("proforma.status_label", { fallback: "Status" })}:
                </span>
                <span
                  style={{
                    fontWeight: 600,
                    // 179 C.2 — the `ready` arm (purple, "Ready to Send") is gone
                    // with the state; C.6 gives the remaining words a dictionary.
                    color:
                      viewInv.paymentStatus === "unpaid"
                        ? "var(--am)"
                        : viewInv.paymentStatus === "partially_paid"
                          ? "var(--am)"
                          : viewInv.paymentStatus === "paid"
                            ? "var(--gn)"
                            : viewInv.paymentStatus === "cancelled"
                              ? "var(--rd)"
                              : "var(--tm)",
                  }}
                  title={getStatusBadgeTooltip(viewInv)}
                >
                  {/* 179 C.6 / 208 §21 — ONE document's status, in words: the
                      mapping lives in src/lib/invoiceStatusWord.ts (209 §16). */}
                  {invoiceStatusWord(viewInv.paymentStatus, t)}
                </span>
                {/* 179 B.6 — the same two words in the same two colours as the
                    list's chip, beside the payment status (ruling 1). */}
                {!isCancelled && (
                  <span
                    style={{
                      fontWeight: 600,
                      padding: "1px 6px",
                      borderRadius: RAD.r10,
                      background: isApproved ? "rgba(167,139,250,.15)" : "rgba(120,120,120,.10)",
                      color: isApproved ? "var(--pp)" : "var(--tm)",
                      border: `1px solid ${isApproved ? "rgba(167,139,250,.3)" : "var(--br)"}`,
                    }}
                  >
                    {approvalWord /* 209 §15 — approved · sent without approval · awaiting it */}
                  </span>
                )}
                {locked && (
                  <Lock
                    size={11}
                    style={{
                      // 179 C.6 [RULED, Laurent's colour rule 2026-09-10] — the
                      // sent-lock takes the GREEN of the list's "Sent" chips
                      // (InvoiceTable, var(--gn)): green already means "went out"
                      // one screen over. Purple is the approval's (B.6), and
                      // would have meant two things in one modal.
                      color:
                        "var(--gn)",
                    }}
                    {...({
                      title:
                        lockTierLabel ||
                        t("proforma.locked", { fallback: "Locked" }),
                    } as any)}
                  />
                )}
              </div>
              {viewInv.approvedBy && (
                <div style={{ fontSize: FS.s9, color: "var(--gn)", marginTop: 2 }}>
                  {t("proforma.approved_by", { fallback: "Approved by" })}{" "}
                  {(() => {
                    const a = co.find((x: any) => x.id === viewInv.approvedBy);
                    return a ? pname(a) : "—";
                  })()}{" "}
                  {t("proforma.approved_on", { fallback: "on" })}{" "}
                  {fd(viewInv.approvedDate)}
                </div>
              )}
            </div>
          </div>
          <div style={{ borderTop: "1px solid var(--br)", paddingTop: 10 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: FS.s11,
                padding: "3px 0",
              }}
            >
              {/* 161C §1 — the same rule as the creation preview, on the
                  saved document. The credited document's number comes from
                  `retainerSources`, which this read already serves (161 §5):
                  for a credit those rows ARE its source, because the ledger
                  hangs the 'credit'-kind allocation on the credited
                  document's own payments (paymentRows.js:416-435). */}
              <span>
                {viewInv.docKind === "credit"
                  ? t("proforma.fees_credit", {
                      number: viewInv.retainerSources?.[0]?.number || "—",
                      fallback: `Credit note on ${viewInv.retainerSources?.[0]?.number || "—"}`,
                    })
                  : viewInv.docKind && viewInv.docKind !== "activities"
                    ? t("proforma.fees_retainer", { fallback: "Retainer (provision)" })
                    : t("proforma.fees", { fallback: "Fees" })}
              </span>
              <span>{fc(viewInv.feeTotal, viewInv.currency)}</span>
            </div>
            {/* 161C §1 — no Costs line on a retainer or a credit. The stored
                row still CARRIES `costsRatio` (19 on PF-2026/020, measured)
                with `subtotalCosts` 0, which is exactly how the row read
                "Costs (19%) €0.00". */}
            {(!viewInv.docKind || viewInv.docKind === "activities") && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: FS.s11,
                padding: "3px 0",
                color: "var(--tm)",
              }}
            >
              <span>
                {t("proforma.costs", { fallback: "Costs" })} (
                {viewInv.costsRatio}%)
              </span>
              <span>{fc(viewInv.costsAmount, viewInv.currency)}</span>
            </div>
            )}
            {viewInv.commercialDeduction > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: FS.s11,
                  padding: "3px 0",
                  color: "var(--am)",
                }}
              >
                <span>
                  {t("proforma.commercial_deduction", {
                    fallback: "Commercial deduction",
                  })}
                </span>
                <span>-{fc(viewInv.commercialDeduction, viewInv.currency)}</span>
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: FS.s11,
                padding: "3px 0",
                borderTop: "1px solid var(--br)",
                marginTop: 4,
                paddingTop: 6,
              }}
            >
              <span>
                {t("proforma.subtotal_ex_vat", {
                  fallback: "Subtotal ex. VAT",
                })}
              </span>
              <span style={{ fontWeight: 600 }}>
                {fc(viewInv.subtotalExVAT, viewInv.currency)}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: FS.s11,
                padding: "3px 0",
                color: "var(--tm)",
              }}
            >
              <span>
                {t("proforma.vat_label", { fallback: "VAT" })} (
                {viewInv.vatRate}%)
              </span>
              <span>{fc(viewInv.vatAmount, viewInv.currency)}</span>
            </div>
            {/* Phase 35 Stage-3 r2 (§4) — paid-retainer deduction is applied
              AFTER VAT (it is an incl.-VAT advance already paid), so the row
              renders below the VAT line and above the total. */}
            {viewInv.retainerDeduction > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: FS.s11,
                  padding: "3px 0",
                  color: "var(--am)",
                }}
              >
                <span>
                  {t("proforma.retainer_deduction", {
                    fallback: "Paid retainer deduction incl. VAT",
                  })}
                  {/* 161 §5 — the deduction names its SOURCES (served on the
                      by-id read: the documents whose paid money it drew). */}
                  {Array.isArray(viewInv.retainerSources) && viewInv.retainerSources.length > 0 && (
                    <span style={{ fontSize: FS.s9, color: "var(--tm)", marginLeft: 6 }}>
                      {viewInv.retainerSources.map((s2: { number: string | null }) => s2.number).filter(Boolean).join(" · ")}
                    </span>
                  )}
                </span>
                <span>-{fc(viewInv.retainerDeduction, viewInv.currency)}</span>
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: FS.s13,
                padding: "6px 0",
                fontWeight: 700,
                borderTop: "2px solid var(--br)",
                marginTop: 4,
              }}
            >
              <span>{t("proforma.total", { fallback: "Total" })}</span>
              <span style={{ color: "var(--ac)" }}>
                {fc(viewInv.totalToPay, viewInv.currency)}
              </span>
            </div>
          </div>
          {/* 161 §7 — the 159 "Sommes détenues sur ce dossier" panel is
              RETIRED: held money lives on retainer documents now, ticked at
              billing time (§5); the deduction line above names the sources. */}
          {viewBank && (
            <div style={{ marginTop: 10, fontSize: FS.s10, color: "var(--tm)" }}>
              <Lbl2>{t("proforma.bank", { fallback: "Bank" })}</Lbl2>
              <div
                style={{
                  fontFamily: "'JetBrains Mono',monospace",
                  color: "var(--ts)",
                  marginTop: 2,
                }}
              >
                {viewBank.iban}
              </div>
            </div>
          )}
        </div>
        {/* 169 G2 [RULED 2026-09-06] — CLICKING A PRO FORMA SHOWS IT; IT DOES
          NOT EDIT IT. This was a textarea that PUT the invoice on blur, with no
          Save and no undo: the one control in the whole view arm that changed
          the document, and it did it silently. The notes are DOCUMENT CONTENT,
          so they stay VISIBLE — hiding them would show less of the document,
          which is the opposite of the ruling — but they are text now, and the
          only way to change them is Edit. (Was: Phase 35 Stage-3 r2 §8, notes
          "rendered and editable in ALL states".) */}
        <div
          style={{
            borderTop: "1px solid var(--br)",
            marginTop: 16,
            paddingTop: 12,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: "6px 12px",
              alignItems: "start",
            }}
          >
            <span style={{ fontSize: FS.s10, color: "var(--tm)", paddingTop: 6 }}>
              {t("proforma.notes", { fallback: "Notes" })}
            </span>
            <span style={{ fontSize: FS.s10_5, whiteSpace: "pre-wrap", paddingTop: 6 }}>
              {viewInv.notes || "—"}
            </span>
          </div>
        </div>
        {/* 179 C.3 — the push-locked block ("Post-push", the order-id editor) STOOD HERE. */}
        <div
          style={{
            borderTop: "1px solid var(--br)",
            marginTop: 16,
            paddingTop: 12,
          }}
        >
          <Lbl2>
            {t("invoice.te.title", { fallback: "Time Entries" })} (
            {viewEntries.length})
          </Lbl2>
          <Table
            style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}
          >
            <thead>
              <tr style={{ borderBottom: "1px solid var(--br)" }}>
                <th
                  style={{
                    textAlign: "left",
                    fontSize: FS.s9,
                    color: "var(--tm)",
                    padding: "3px 4px",
                  }}
                >
                  {t("proforma.print.col_date", { fallback: "Date" })}
                </th>
                <th
                  style={{
                    textAlign: "left",
                    fontSize: FS.s9,
                    color: "var(--tm)",
                    padding: "3px 4px",
                  }}
                >
                  {t("proforma.print.col_activity", { fallback: "Activity" })}
                </th>
                <th
                  style={{
                    textAlign: "left",
                    fontSize: FS.s9,
                    color: "var(--tm)",
                    padding: "3px 4px",
                  }}
                >
                  {t("proforma.print.col_description", {
                    fallback: "Description",
                  })}
                </th>
                <th
                  style={{
                    textAlign: "right",
                    fontSize: FS.s9,
                    color: "var(--tm)",
                    padding: "3px 4px",
                  }}
                >
                  {t("proforma.print.col_minutes", { fallback: "Minutes" })}
                </th>
              </tr>
            </thead>
            <tbody>
              {[...viewEntries]
                .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
                .map((e) => (
                  <tr
                    key={e.id}
                    style={{ borderBottom: "1px solid rgba(255,255,255,.04)" }}
                  >
                    <td style={{ fontSize: FS.s10, padding: "3px 4px" }}>
                      {fd(e.date)}
                    </td>
                    <td style={{ fontSize: FS.s10, padding: "3px 4px" }}>
                      {actName2(e.type)}
                    </td>
                    <td
                      style={{
                        fontSize: FS.s10,
                        padding: "3px 4px",
                        color: "var(--ts)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: 280,
                      }}
                    >
                      {e.desc || "—"}
                    </td>
                    <td
                      style={{
                        fontSize: FS.s10,
                        padding: "3px 4px",
                        textAlign: "right",
                        fontFamily: "'JetBrains Mono',monospace",
                      }}
                    >
                      {e.min || 0}
                    </td>
                  </tr>
                ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: "1px solid var(--br)" }}>
                <td
                  colSpan={3}
                  style={{ fontSize: FS.s10, padding: "4px", fontWeight: 600 }}
                >
                  {t("proforma.total", { fallback: "Total" })}
                </td>
                <td
                  style={{
                    fontSize: FS.s10,
                    padding: "4px",
                    textAlign: "right",
                    fontWeight: 600,
                    fontFamily: "'JetBrains Mono',monospace",
                  }}
                >
                  {totalMin} {t("proforma.min_unit", { fallback: "min" })} (
                  {fmin(totalMin)})
                </td>
              </tr>
            </tfoot>
          </Table>
        </div>
        {viewInv.paymentConditions && (
          <div
            style={{
              marginTop: 8,
              fontSize: FS.s10,
              color: "var(--tm)",
              fontStyle: "italic",
            }}
          >
            {viewInv.paymentConditions}
          </div>
        )}
      </ModalLayout>
    );
  }

  // ============ CREATE MODE ============
  // 163 SB.1 - create mode is a CHILD COMPONENT now. It held eleven hooks
  // BELOW this function's two conditional returns (the view-mode return and
  // the !cs bailout) - a rules-of-hooks violation on every one. The one shape
  // of the fix: the guarded tail becomes a component of its own, mounted
  // after the guards, so its hooks are unconditional. Module scope for stable
  // identity (Bug-A). Behaviour identical - the !cs bailout runs HERE, after
  // every parent hook, before the child mounts.
  const csId = pfMod.csId;
  const csType = pfMod.csType;
  const cs =
    csType === "cc"
      ? (cc || []).find((c: { id: number }) => c.id === csId)
      : (ca || []).find((c: { id: number }) => c.id === csId);
  if (!cs) {
    onClose();
    return null;
  }
  return (
    <ProFormaCreate
      pfMod={pfMod}
      onClose={onClose}
      te={te}
      sTe={sTe}
      ca={ca}
      cc={cc}
      cl={cl}
      inv={inv}
      sInv={sInv}
      toast={toast}
      tkTypes={tkTypes}
      firmSettings={firmSettings}
    />
  );
}

function ProFormaCreate({
  pfMod,
  onClose,
  te,
  sTe,
  ca,
  cc,
  cl,
  inv,
  sInv,
  toast,
  tkTypes,
  firmSettings,
}: any) {
  // 170 §3c — `lang` for the per-language conditions prefill below; 206E §4.3 —
  // `tIn` for the words written in the CLIENT's language instead.
  const { t, lang, tIn } = useTranslation();
  const getInvSettings = () => firmSettings || {};
  const settings = getInvSettings();
  const firm = settings.firm || {};
  /* 207 §12 [RULED] — the firm block below is a SCREEN, so it reads the
     READER's language: the street and the city by their variant when one is
     filled, the country (a code) as its word (the document prints the
     document's language — server/src/lib/docGen.js `firmAddressLine`). */
  const firmA = firmAddressIn(firm, lang);
  const countryEntries = useTaxonomy("country").entries;
  const firmCountryShown: unknown = firmA.country ? resolveTaxonomyLabel(firmA.country, countryEntries) : "";
  const banks = settings.bankAccounts || [];
  // 179 C.9 — `pfx` STOOD HERE; it fed only the removed number prediction.
  const dueDays = settings.dueDays ?? 30;
  const defVat = settings.vatRate ?? 21;
  // Phase 35 Stage-3 r2 (§5) — the "Particular conditions" prefill source: the
  // firm template (Settings) or the standard default. Variables resolved below.
  /* 170 §3c [RULED 2026-09-08, Laurent] — the template is a PER-LANGUAGE map
     now, not one string for everybody. It was read in the language the LAWYER
     is reading — SUPERSEDED by 206E §4.3 [RULED 2026-09-23]: the prefill is
     picked in the CLIENT's language (ruling 169 G4: a document's language is
     the client's), because what it prefills is printed on the client's
     document as `{paymentConditions}`. It is still an editable field the lawyer
     approves before saving, and what is stored is the substituted result per
     invoice. The prefill itself now sits below `primaryClient`, whose language
     it reads. A legacy plain string still
     reads correctly: the migration wrote it under all three keys, and the
     `typeof` guard below accepts one anyway.
     THE DEFAULT ALSO CHANGED, and the two edits belong together: `{costsRatio}`
     is added and the payment-delay sentence is GONE, because
     `resolveConditionsText` resolved `{paymentDelay}` against the firm-wide
     `FirmSettings.dueDays` while the PDF now prints the invoice's own
     `{dueDate}` from the modal. The two could contradict each other on one page;
     dropping the sentence removes the contradiction instead of papering over
     it. A NEW key, because the seed only inserts and never rewrites. */
  const logo = settings.logo || null;
  const gcAll = settings.generalConditions || {};
  // Phase 35 (C1 / L2) — manual pro-forma-number override is opt-in per firm.
  // Default OFF → the number is server-allocated on save; ON → the field below
  // is editable and its value is sent to the allocator-bypass path on the server.
  const allowManual = !!settings.proformaAllowManualNumber;

  // Permission checks — Phase auth-c2: "Send to Accounting" is part of
  // the invoice lifecycle, server-gated by lateral.invoices (requireSection
  // on the /api/invoices routes). Mirror that gate on the button so FE
  // and server stay aligned without minting a new section key. Admin
  // always passes via the isAdmin overlay inside can().
  const _pfPerms = usePermissions();
  const canSendToAccounting = _pfPerms.can("lateral.invoices.view");

  // Resolve case
  const csId = pfMod.csId;
  const csType = pfMod.csType;
  const cs =
    csType === "cc"
      ? (cc || []).find((c: { id: number }) => c.id === csId)
      : (ca || []).find((c: { id: number }) => c.id === csId);

  // Resolve entries
  const entries = te.filter((e: any) => pfMod.entryIds.includes(e.id));

  // 161 §4 — WHICH KIND OF DOCUMENT the modal is creating. 'retainer' arrives
  // from the second Invoices-block button ({ entryIds: [], docKind }); §6's
  // Reimbursement checkbox flips it to 'credit' (totals negative
  // through the same ladder). Everything that is the SAME modal (bank,
  // language, conditions, notes, save) stays untouched — the retainer mode
  // only swaps the ANNEXE for one money field, hides the period selector,
  // and pins the entry list empty.
  const baseKind = pfMod.docKind === "retainer" ? "retainer" : "activities";
  const isRetainer = baseKind === "retainer";
  const [refundMode, setRefundMode] = useState(false);
  const [refundSourceId, setRefundSourceId] = useState<number | null>(null);
  const docKind = isRetainer && refundMode ? "credit" : baseKind;

  // Resolve client(s)
  const clientIds = cs.clIds || [];
  const clientLinks = cs.clientLinks || [];
  const clients = clientIds
    .map((id: any) => cl.find((c: any) => c.id === id))
    .filter(Boolean);
  const ccClients = clientLinks
    .map((lk: any) => {
      const c = cl.find((x: any) => x.id === lk.clId);
      return c ? { ...c, _lk: lk } : null;
    })
    .filter(Boolean);
  const allClients = clients.length > 0 ? clients : ccClients;
  /* 213B §11 [RULED 44] — A NON-COLLECTIVE CASE WITH SEVERAL CLIENTS: a row of
     boxes, one per client in the case's order; only the first is ticked when
     the window opens (an edit of an unsent pro forma reopens with its stored
     ticks). The FIRST TICKED is the pro forma's client — its id, address,
     snapshot parts, VAT, bank and the document's language; the addressee line
     names every ticked client. A one-client case and a collective case show no
     boxes and behave exactly as before (src/lib/proFormaAddressees.ts). */
  const multiClient = Array.isArray(clients) && clients.length > 1;
  const allClientsU: unknown = allClients; // the untyped list, handed to the readers as unknown (the ratchet counts an any[] argument)
  const [tickedIds, setTickedIds] = useState<number[] | null>(null);
  const effTicked: number[] = tickedIds ?? initialTickedIds(allClientsU, editSnapshotOf(pfMod));
  const primaryIndex = multiClient ? firstTickedIndexOf(allClientsU, effTicked) : 0;
  const primaryClient = allClients[primaryIndex] || {};
  /* 206E §4.3 [RULED 2026-09-23] — THE CLIENT'S LANGUAGE, for the two things
     this window writes INTO the document at creation: the person title inside
     `clientName` (it was resolved in the reader's language) and the particular
     conditions prefill (170 §3c's, above). `Client.language`, normalised by the
     server's own rule (src/lib/docLang.mjs `normalizeLang`, docGen.js requires
     the same file).
     208 §11 [RULED 9, 2026-09-24] — AND FRENCH WHEN THE CLIENT HAS NONE, never
     the reader's: the reader's language stood here, so an English reader wrote
     « Mrs » and English conditions where a Dutch one wrote « Mevr. » and Dutch —
     and the server prints that client's document in French. One rule, the
     server's (`clientDocumentLanguage`); the client's language read as stored
     (`language`, or the `lang` a row this page saved carries — 207C §6). An
     edit keeps the stored conditions verbatim (169 G3) and writes the title in
     the same language, so whoever reopens the document, its words stay.
     209 §5 [RULED 18] — A CLIENT WITHOUT A LANGUAGE READS ITS CASE'S (it was
     French). Neither has one: this window cannot write them, so it says so —
     « Choisissez d'abord la langue du dossier », the case form one click away —
     and saves nothing (never a guess); what it draws meanwhile is in the
     reader's language, a screen only. */
  const pcLangOf = clientDocumentLanguage(primaryClient, cs);
  const pcLang: string = pcLangOf ?? lang;
  // 213B §11 — the ticked clients as the snapshot stores them (named in the document's language), and the boxes' rows
  const nameInDocLang = (c: unknown): string => String(pnameIn(c, pcLang));
  const tickedRows = multiClient ? tickedInOrder(allClientsU, effTicked) : [];
  const addressees = multiClient ? addresseesOf(tickedRows, tickedRows.map(nameInDocLang)) : [];
  const clientRows = multiClient ? clientRowsOf(allClientsU, asRows(allClientsU).map(nameInDocLang)) : [];
  // the join word in the DOCUMENT's language (`tIn`, 206E §4.3's seam), never the reader's
  const addresseeLine = multiClient ? joinNames(addressees.map((a) => a.name), String(tIn(pcLang, "proforma.addressees.join", { fallback: "and" }))) : "";
  const pcTpl = settings.particularConditionsTemplate;
  const payTpl =
    (typeof pcTpl === "string" ? pcTpl : null) ||
    (pcTpl && typeof pcTpl === "object" ? (pcTpl[pcLang] || pcTpl.en || pcTpl.fr || "") : "") ||
    tIn(pcLang, "proforma.particular_default2", {
      fallback:
        "Reference hourly rate: {referenceRate} EUR/h. Fixed costs: {costsRatio}%. Success fee: {successFeeRate}%, provisions deducted.",
    });

  // Case costs ratio (from the case, not global settings)
  const caseCR = cs.costsRatio ?? 19;

  // 179 C.9 [RULED 2026-09-10] — THE BROWSER NO LONGER PREDICTS THE NUMBER.
  // It used to compute max+1 over the rows THIS viewer could see (else
  // `settings.startingNumber`) and show it as the document's number, while the
  // server allocates from `proformaCounter` and never read startingNumber —
  // two numbering systems, and with manual override on, the browser's guess
  // was stored. The create form now says the number is assigned on save; the
  // real one shows after. An EDIT shows the stored number (`pfMod.remakeNumber`).
  const assignedOnSave = t("proforma.number_assigned_on_save", { fallback: "Assigned on save" });

  const today = new Date().toISOString().slice(0, 10);
  const dueDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + dueDays);
    return d.toISOString().slice(0, 10);
  })();

  // Compute exact period from entries
  const entryDates = entries
    .map((e: any) => e.date)
    .filter(Boolean)
    .sort();
  const exactFrom = entryDates[0] || today;
  const exactTo = entryDates[entryDates.length - 1] || today;

  // 169 G3 [RULED 2026-09-06] — EDIT IS THE CREATE FORM, ON THE DOCUMENT. The
  // edit arm prefilled from the CASE and the FIRM and never from the row it was
  // about, so opening PF-2026/031 and pressing Save with nothing typed rewrote
  // it. MEASURED on the fixture tenant, one click, nothing typed: costsRatio
  // 5 → 19 (the case's), vatRate 0 → 21 (the firm default), retainerDeduction
  // 300 → 0, commercialDeduction 100 → 0, notes and particular conditions
  // replaced, issueDate moved to today — and totalAmount 1 700 → 2 879,80.
  // (The FEES survived: the server falls back to the stored stated total for a
  // foreign document, invoices.js:840. Everything around them did not.)
  //
  // `doc` is the read-set the Edit button hands over. `pick` returns the
  // document's value when there IS one and the create default otherwise, so a
  // create is byte-for-byte what it was and an edit is the document.
  const editDoc: Record<string, unknown> | null =
    pfMod.editId && pfMod.doc && typeof pfMod.doc === "object" ? pfMod.doc : null;
  const pick = (k: string, dflt: unknown) => {
    if (!editDoc) return dflt;
    const v = editDoc[k];
    return v === undefined || v === null ? dflt : v;
  };
  // Stored dates arrive as ISO instants; the inputs want yyyy-mm-dd.
  const ymd = (v: unknown, dflt: string) =>
    typeof v === "string" && v.length >= 10 ? v.slice(0, 10) : dflt;

  // Form state
  const [form, setForm] = useState<any>({
    // 169 R4 — the document's currency, chosen at issue: the firm's unless
    // the caller opened the modal with one (an EDIT re-save keeps the
    // document's; the server ignores a PUT's currency anyway).
    currency: pick("currency", (typeof pfMod.currency === "string" && pfMod.currency) || firmCurrencyOf()) as string,
    // 169 R5 — a FOREIGN-currency activities document is priced by a STATED
    // fee total (ex-VAT, in the document's currency); unread otherwise.
    // 169 R5/G3 — on a FOREIGN activities document the stated total IS the
    // stored `subtotalFees`: that is the number that was stated at issue, and
    // re-deriving it from the entries at the firm's rates would re-price the
    // document. A firm-currency document keeps deriving its fees from the
    // entries, so it prefills 0 and the field is not shown.
    statedFeeTotal: Number(pick("subtotalFees", 0)) || 0,
    // 161 §4 — the retainer's ex-VAT lump sum (R1); required > 0 in retainer
    // mode, unread in activities mode.
    retainerAmount: Math.abs(Number(pick("subtotalFees", 0))) || 0,
    costsRatio: pick("costsRatio", caseCR),
    vatRate: pick("vatRate", defVat),
    // 169 G3 — the stored deduction ROUND-TRIPS. It was zeroed on every edit
    // while the consumption ledger still recorded the draw, so the document and
    // the ledger disagreed the moment anyone pressed Save. Changing it in edit
    // mode is a separate question — see the sources block below.
    retainerDeduction: Number(pick("retainerDeduction", 0)) || 0,
    // Phase 35 Stage-3 — informal pre-VAT commercial deduction (replaces credits).
    commercialDeduction: Number(pick("commercialDeduction", 0)) || 0,
    bankIdx: Number(pick("bankAccountIdx", 0)) || 0,
    notes: (pick("notes", "") as string) || "",
    // Phase 35 Stage-3 — "Particular conditions" prefill (fully editable below):
    // the case reference hourly rate + success-fee % + the standard payment delay
    // (FirmSettings.dueDays). Uses the firm payment template when set, else a
    // composed default. This field also carries the commercial-deduction note.
    // 169 G3 — an EDIT shows the document's own conditions VERBATIM. The
    // token substitution below belongs to the PREFILL, not to stored text: a
    // saved string has had its tokens resolved once already, and running the
    // chain over it again would rewrite any brace the lawyer typed.
    paymentConditions: editDoc && typeof editDoc.paymentConditions === "string"
      ? editDoc.paymentConditions
      : (
        payTpl ||
        tIn(pcLang, "proforma.particular_default", { // 206E §4.3 — the client's language
          fallback:
            "Reference hourly rate: {referenceRate} EUR/h. Success fee: {successFeeRate}%. Payment due within {paymentDelay} days.",
        })
      )
        .replace(
          "{referenceRate}",
          cs.referenceHourlyRate != null ? fq(cs.referenceHourlyRate) : "—",
        )
        .replace("{successFeeRate}", String(successFeeRateOf(cs)))
        .replace("{costsRatio}", String(caseCR))
        .replace(/\{paymentDelay\}|\{dueDays\}/g, String(dueDays)),
    invoiceDate: ymd(editDoc ? editDoc.issueDate : null, today),
    dueDate: ymd(editDoc ? editDoc.dueDate : null, dueDate),
    periodMode: (pick("periodMode", "exact") as string) || "exact",
    periodFrom: ymd(editDoc ? editDoc.periodFrom : null, exactFrom),
    periodTo: ymd(editDoc ? editDoc.periodTo : null, exactTo),
    // Phase 35 (C1 / L2) — provisional number; editable only when allowManual.
    // Phase 35 Stage-3 — a "remake" of a cancelled pro forma inherits its
    // number by default (shown in the manual-number field when the firm allows
    // manual numbering). Otherwise the provisional allocator number is shown.
    // 179 C.9 — empty on a create: the allocator owns the number unless typed.
    numberOverride: pfMod.remakeNumber || "",
  });
  // Phase 35 (C1) — in-flight guard for the async create (the server allocates
  // the number + binds the entries atomically); blocks a double-submit minting
  // two pro formas.
  const [saving, setSaving] = useState(false);
  const uf = (k: any, v: any) => setForm((p: any) => ({ ...p, [k]: v }));
  // 169 R3/R5 — the firm's currency, and whether this document is priced
  // by a stated total (foreign currency, activities kind).
  const firmCur = firmCurrencyOf();
  const foreignCur = form.currency !== firmCur;
  const statedMode = foreignCur && docKind === "activities";
  // 169 R8 — a label that spells the currency: the old key on a firm-currency
  // document (byte for byte), its `_cur` twin with {currency} otherwise.
  const curLabel = (key: string, fallback: string, curFallback: string) =>
    foreignCur
      ? t(`${key}_cur`, { currency: form.currency, fallback: curFallback.replace("{currency}", form.currency) })
      : t(key, { fallback });

  // Period display computation — 206E §4.3: the builder moved to
  // src/lib/periodLabel.mjs, the one definition the server reads too; the
  // window shows (and stores) it in the READER's language, the document prints
  // it in the CLIENT's, built at generation from the same inputs.
  const periodDisplay = (() => {
    const mode: unknown = form.periodMode;
    // 207 §11 [RULED] — the quarter's word from the dictionary, the reader's language (« T2 2026 », « K2 2026 »).
    const quarter = (n: number) => (t as Translate)("proforma.period.quarter_short", { n, fallback: "Q{n}" }); // `t` is untyped here; the seam narrows it
    return periodLabel(mode, mode === "custom" ? form.periodFrom : exactFrom, mode === "custom" ? form.periodTo : exactTo, lang, quarter);
  })();

  // Financial calculations — C.2 (BRIEF 04): the money ladder moved
  // server-side (POST /invoices/proforma/compute; same arithmetic, verbatim,
  // recording-verified). The modal sends the INPUTS — the entry ids in ITS
  // OWN iteration order (the reduce order the browser used; float summation
  // is order-sensitive) plus the raw form money fields — and displays the
  // returned unrounded doubles through the same fc/fq formatting. The modal
  // computes no money. Latest-wins: a stale response never overwrites a
  // newer one. Money rows render fc(null) (€0.00) until the first response.
  const entryIdsOrdered = isRetainer ? [] : entries.map((e: any) => e.id);
  const [calc, setCalc] = useState<any>(null);
  // 161 §5 — THE SOURCES (activities mode only): the matter's retainers and
  // cancelled documents still holding paid money, SERVED with their live
  // `unconsumed` — never derived here. One checkbox per source; the ticked
  // amounts SUM into the ladder's deduction input (same name, no manual
  // field). An amount is editable DOWNWARD only: its ceiling is
  // min(unconsumed, what this document's gross can still absorb).
  type SourceDoc = {
    id: number; number: string | null; docKind: string; cancelled: boolean;
    paidTotal: number; applied: number; refunded: number; unconsumed: number;
    currency?: string | null;
  };
  const [sourceDocs, setSourceDocs] = useState<SourceDoc[] | null>(null);
  // 171 §1e — the state takes `SourceTicks`, the lib's own type, whose value
  // admits `undefined` DELIBERATELY (invoiceSources.ts): an id with no tick is
  // the ordinary case, and a Record declared always-present tells the reader
  // and the linter a lie that module exists to stop believing. Widened rather
  // than the seed narrowed — never approximate.
  const [srcSel, setSrcSel] = useState<SourceTicks>({});
  // 171 §1b [RULED 2026-09-07, Laurent] — THE PICKER ENTERS EDIT MODE. 169 G3
  // left it out and said why: offering sources on an edit meant releasing and
  // re-writing a ledger on a route that had never touched one, and the policy
  // — what happens to a draw whose source has since been spent, or paid, or
  // credited — was Laurent's to set. He set it: an edit of an un-sent pro forma
  // MAY change which retainers it applies, and the route releases its own
  // consumption rows and re-draws from the submitted set in one transaction
  // (171 §1d, `invoices.js` PUT /:id). This is the screen where that is shown,
  // and per Laurent it is "the most obvious need for an edit".
  //
  // 171 §1e — AN EDIT WHOSE OWN DRAWS ARE UNKNOWN DOES NOT SPEAK FOR THEM. A
  // pre-161 document can carry a stored `retainerDeduction` with no
  // allocations behind it, and its served `retainerSources` is then null. The
  // picker stays OUT of that edit, `sources` stays out of the payload, and the
  // server leaves the ledger alone (a body with no `sources` key is not a body
  // with an empty one) — so the stored deduction round-trips exactly as 169 G3
  // made it, rather than the picker seeding empty and zeroing a real draw.
  // A document with no draws AND no deduction is not that case: it seeds empty
  // and Laurent can tick a retainer onto it, which is the point.
  const editDraws = pfMod.editId ? drawsIn(pfMod.doc?.retainerSources) : null;
  const unledgeredDeduction = !!pfMod.editId && !editDraws && Number(form.retainerDeduction) > 0;
  const sourcesActive = !isRetainer && !unledgeredDeduction;
  /* 212 §22 — asked the 210 §1 way; still failing, the sources say « La liste
     n'a pas pu être chargée » with « Réessayer » where they show — never
     nothing at all (activities), never « No paid document on this matter can
     be credited » (a credit note) when they were never read. */
  const { entry: srcRead, again: srcAgain, ask: askSources } = useReadMark(PAGE_LISTS.invoiceSources);
  const srcLists = usePageListContext(srcRead);
  useEffect(() => {
    // Fetched for BOTH modes: the activities create ticks them (§5), the
    // retainer modal's Reimbursement arm picks ONE of them (§6).
    if (!sourcesActive && !isRetainer) return;
    let live = true;
    // 169 R6 — SAME CURRENCY OR NOTHING: only the sources in this document's
    // currency are offered; the list refetches when the currency changes.
    // 171 §1e — on an EDIT the offer is measured as if this document had
    // already released its claims, so a source it has fully consumed is still
    // on the list and can be unticked. Its own release, in the transaction,
    // is what makes that view true at save time.
    void askSources("the invoice sources", () =>
      getInvoiceSources(csType === "ca" ? csId : null, csType === "cc" ? csId : null, isRetainer ? "credit" : undefined, form.currency, pfMod.editId || null),
    ).then((rows) => {
        if (!live) return;
        if (!rows) {
          setSourceDocs([]);
          setSrcSel({});
          setRefundSourceId(null);
          return;
        }
        const list = rows as SourceDoc[];
        setSourceDocs(list);
        // 169 GLANCE 1 — A SELECTION DOES NOT SURVIVE ITS LIST. Both selections
        // are keyed by invoice id and both lists are refetched here, so a pick
        // made against the previous currency's offer would otherwise outlive
        // the rows the user can see. Set in the SAME commit as the new
        // list, never before it: while the fetch is in flight `sourceDocs` is
        // still the list the ticks belong to, and the sums stay right for it.
        //
        // 171 §1e — and on an EDIT the ticks are not cleared but SEEDED, from
        // what the document currently draws. The seed is taken OVER THE NEW
        // LIST for the same reason the clear is: a draw whose source is not on
        // the offer is not a tick, whatever the document says. Re-seeding on
        // every population is deliberate and idempotent — the selection is
        // always derived from the list just committed.
        setSrcSel(seedTicks(list, editDraws));
        setRefundSourceId(null);
      });
    return () => { live = false; };
  }, [csId, csType, sourcesActive, isRetainer, form.currency, srcAgain, askSources]);
  // 169 GLANCE 1 — ONE POPULATION FOR ALL THREE READERS. The ladder's names,
  // the Σ it prints and every row's ceiling are taken over the SAME list, so a
  // sum can never see a row the user cannot. `tickedSources` is what the
  // compute is sent (below) and what the ladder names.
  const tickedSources = tickedIn(sourceDocs, srcSel);
  const tickedBody = tickedSources.map((s) => ({ invoiceId: s.id, amount: srcSel[s.id]?.amount ?? 0 }));
  const ticksKey = JSON.stringify(tickedBody);
  // An edit's offer is measured as if this document had released its claims
  // (171 §1e) — the compute's `forInvoiceId`, as GET /sources's.
  const editIdOf: unknown = pfMod.editId;
  const forInvoiceId = Number(editIdOf) > 0 ? Number(editIdOf) : null;
  // 209 §2 — THE SERVER OWNS BOTH HALVES OF THE CEILING, and the modal displays
  // what it is handed: the compute is sent the ticks and answers the Σ the
  // document will be charged and every offered source's ceiling —
  // min(unconsumed, what the gross can still absorb once the OTHER ticks have
  // taken theirs), in cents (routes/invoices.js /proforma/compute,
  // lib/invoiceSourceDraws.js). The save asks the same question and refuses a
  // Σ above the gross (409 `invoice.sources_exceed_gross`). Until the first
  // answer a row has no ceiling (0).
  const served = servedSourceFigures(calc);
  const sourcesTotal = sourcesActive ? served.total : 0;
  const srcCeiling = (id: number) => served.ceilings.get(id) ?? 0;
  const effDeduction = sourcesActive ? sourcesTotal : form.retainerDeduction;

  // 161 §6 — the reimbursement's ONE source. Auto-selected when the matter
  // holds exactly one; picking (or auto-picking) prefills the amount with its
  // unconsumed balance, editable DOWNWARD only.
  const refundSrc = (sourceDocs || []).find((s) => s.id === refundSourceId) || null;
  // §6/§10 — a source's `unconsumed` is PAID money (VAT-incl); the amount
  // field is ex-VAT and the refund DOCUMENT must total the refunded money
  // (R7's netting), so the prefill and the ceiling convert at this
  // document's VAT rate. The server draws |totalAmount| from the source.
  // RAW quotient, deliberately unrounded: a cent-rounded ex-VAT re-multiplied
  // by the VAT lands 0.38 milli-cents past the cent, and a document that
  // cannot be settled to 'paid' through the cent-rounding payment route is
  // worse than a long decimal in the input (the preview displays rounded).
  const refundExOf = (unconsumed: number) =>
    unconsumed / (1 + (Number(form.vatRate) || 0) / 100);
  useEffect(() => {
    // `refundSourceId` is READ but deliberately not a dep: this effect is an
    // auto-select that fires when the mode flips or the list arrives, and an
    // effect that sets a state it depends on re-enters itself (the lint's
    // brief-78 class). The stale-closure read is exactly the semantics: skip
    // when a pick already existed at that render.
    if (!refundMode || !sourceDocs) return;
    if (refundSourceId == null && sourceDocs.length === 1) {
      setRefundSourceId(sourceDocs[0].id);
      uf("retainerAmount", refundExOf(sourceDocs[0].unconsumed));
    }
  }, [refundMode, sourceDocs]);
  useEffect(() => {
    let live = true;
    computeProForma({
      caseId: csType === "ca" ? csId : null,
      ccId: csType === "cc" ? csId : null,
      entryIds: entryIdsOrdered,
      costsRatio: form.costsRatio,
      vatRate: form.vatRate,
      commercialDeduction: form.commercialDeduction,
      // 161 §5 — the deduction IS the Σ of the ticked sources. 209 §2 — the
      // server takes that Σ over its own offer (an edit's measured as if
      // released, 171 §1e) and answers it with every row's ceiling; the modal
      // sends the ticks, never a sum. The form value survives only where the
      // picker is absent: retainer mode, and the unledgered edit §1e keeps
      // out of (the stored deduction, which the server keeps).
      ...(sourcesActive
        ? { sources: tickedBody, forInvoiceId }
        : { retainerDeduction: form.retainerDeduction }),
      // 161 §4 — the kind + the typed lump sum ride the same compute; the
      // server's ladder pins entries/costs/minutes for the retainer kinds.
      docKind,
      retainerAmount: form.retainerAmount,
      // 169 R4/R5 — the document's currency decides whether a stated total is
      // lawful; a foreign activities document sends its stated fee total.
      currency: form.currency,
      ...(statedMode ? { statedFeeTotal: form.statedFeeTotal } : {}),
    })
      .then((r) => {
        if (live) setCalc(r);
      })
      .catch(() => {
        if (live) setCalc(null);
      });
    return () => {
      live = false;
    };
  }, [
    entryIdsOrdered.join(","),
    form.costsRatio,
    form.vatRate,
    form.commercialDeduction,
    form.retainerDeduction,
    form.retainerAmount,
    form.currency,
    form.statedFeeTotal,
    statedMode,
    sourcesActive,
    ticksKey,
    forInvoiceId,
    docKind,
    csId,
    csType,
  ]);
  const feeTotal = calc ? calc.feeTotal : null;
  const costsAmount = calc ? calc.costsAmount : null;
  const vatAmount = calc ? calc.vatAmount : null;
  const totalToPay = calc ? calc.totalToPay : null;
  const afterRetainer = calc ? calc.subtotalExVat : null; // subtotalExVAT display value (pre-VAT base)
  const totalMinutes = calc ? calc.totalMinutes : 0;


  const bank = banks[form.bankIdx] || null;

  // Activity type lookup — show full name in attachment
  const actName = (code: any) => {
    if (!code) return "—";
    const t = (tkTypes || []).find((x: any) => x.code === code);
    return t ? t.desc : code;
  };

  // Phase 35 (C1 / L1+L2) — the SINGLE create path now delegates to the server:
  // POST /invoices/proforma allocates the number from the atomic counter (L2)
  // and binds the selected entries (invoiceId + mark='billable') in one
  // transaction (L1). The client no longer computes the number (max+1) nor marks
  // entries billed (that was a server-dropped no-op). We only reconcile the
  // in-memory caches from the response; the server also fires inv/te pokes.
  // 169 R1 — the payload carries INPUTS only: `status` and `createdBy` left
  // it (the server coerces a new document to 'unpaid' and stamps the creator
  // from the session; neither is admitted off the body any more).
  const saveInvoice = async () => {
    if (saving) return;
    // 213B §11 [RULED 44] — at least one client must stay ticked: creating with none is refused and said
    if (multiClient && !addressees.length) {
      sayPf((t as Translate)("proforma.addressees.none", { fallback: "Tick at least one client." }), "error");
      return;
    }
    setSaving(true);
    const caseAbbrev = (cs.title || "").split(/\s+/).slice(0, 2).join(" ");
    const payload: any = {
      type: "proforma",
      caseId: csType === "ca" ? csId : null,
      ccId: csType === "cc" ? csId : null,
      caseRef: cs.ref || "",
      caseTitle: cs.title || "",
      caseAbbrev,
      caseType: csType,
      clientId: primaryClient.id || null,
      // 206E §4.3 — the title in the CLIENT's language, not the reader's; 213B §11 [RULED 44] — every ticked client
      // where several are, joined in the document's language (« SA X et M. Y »): the document's {clientName} prints it
      clientName: multiClient ? addresseeLine : String(pnameIn(primaryClient, pcLang)),
      clientAddress: [
        primaryClient.street,
        primaryClient.postalCode,
        primaryClient.city,
        primaryClient.country,
      ]
        .filter(Boolean)
        .join(", "),
      // Phase 40 — structured address SNAPSHOT (raw parts, as invoiced) for the
      // {clientStreet}/{clientPostcode}/{clientCity}/{clientCountry} markers.
      // 179 C.7 — the comment here said the server "packs this into the dead
      // `lines` column"; it does not: the server writes it to its own
      // `clientAddressSnapshot` Json column (invoices.js, the create), and
      // `lines` is no longer written by anything (it is dropped in C.9).
      clientAddressSnapshot: {
        street: primaryClient.street || "",
        postalCode: primaryClient.postalCode || "",
        city: primaryClient.city || "",
        country: primaryClient.country || "",
        email: primaryClient.email || "",
        // 213B §11 [RULED 44] — the addressees as invoiced, in the ticked order (none stored for a one-client case)
        ...(multiClient ? { addressees } : {}),
        // Phase 40 — snapshot the SELECTED bank account so {bankInstitution}/
        // {bankIBAN}/{bankBIC} render AS INVOICED (immune to later reorder/delete).
        bankAccount: bank
          ? { name: bank.name || "", iban: bank.iban || "", bic: bank.bic || "", bankName: bank.bankName || "" }
          : null,
      },
      clientVAT: primaryClient.vat || "",
      invoiceDate: form.invoiceDate,
      dueDate: form.dueDate,
      // 161 §4 — the kind rides the create; a retainer carries NO period
      // (`periodDisplay` is not written — the list shows the document kind in
      // that cell instead) and the server scrubs the fields besides.
      docKind,
      // 169 R4/R5 — chosen at issue; the server validates and defaults it, and
      // ignores it on a PUT. The stated fee total rides only in stated mode.
      currency: form.currency,
      ...(statedMode ? { statedFeeTotal: form.statedFeeTotal } : {}),
      ...(docKind === "credit" ? { refundSourceId } : {}),
      ...(isRetainer
        ? { retainerAmount: Number(form.retainerAmount) || 0 }
        : {
            periodMode: form.periodMode,
            periodFrom: form.periodMode === "custom" ? form.periodFrom : exactFrom,
            periodTo: form.periodMode === "custom" ? form.periodTo : exactTo,
            periodDisplay,
          }),
      // C.2 (BRIEF 04) — send only the INPUTS: the entry ids in the modal's
      // iteration order (the browser's old reduce order) + the money form
      // fields. The five ladder money columns (feeTotal/costsAmount/
      // subtotalExVAT/vatAmount/totalToPay) are computed AND stored
      // server-side on create/edit; the browser no longer sends them.
      entryIds: entryIdsOrdered,
      costsRatio: form.costsRatio,
      // 209 §2 — a deduction rides ONLY where the picker is absent (retainer
      // mode; the unledgered edit, whose stored figure the server keeps): the
      // ticked set below is the deduction everywhere else, and a bare one on a
      // create is refused (400 `invoice.deduction_needs_sources`).
      ...(sourcesActive ? {} : { retainerDeduction: form.retainerDeduction || 0 }),
      commercialDeduction: form.commercialDeduction || 0,
      vatRate: form.vatRate,
      bankAccountIdx: form.bankIdx,
      paymentConditions: form.paymentConditions,
      notes: form.notes,
    };
    // 161 §5 — the ticked sources ride the create; the server validates each
    // amount against the live `unconsumed` and writes the consumption ledger
    // in the same transaction (409 `invoice.source_exhausted` if it moved).
    // 171 §1d — and they ride the EDIT, where the route releases this
    // document's own claims first and re-draws from what is sent. An EMPTY
    // array is meant: it says "untick everything" and the server releases.
    // Sending nothing at all is a different act, and it is the one §1e's
    // unledgered edit performs — `sourcesActive` is false there and this
    // block does not run, so the stored deduction and its ledger are left
    // exactly as they are.
    if (sourcesActive) {
      payload.sources = tickedBody;
    }
    // L2 — only send a number when the firm has opted into manual override AND
    // the user supplied one; otherwise the server allocator owns the number.
    if (allowManual && (form.numberOverride || "").trim()) {
      payload.number = form.numberOverride.trim();
    }
    try {
      // §1/§2 — Edit re-saves an existing un-sent pro forma via PUT (same number
      // + entries); otherwise create a new one via the allocator.
      let created;
      if (pfMod.editId) {
        const r = await editProForma(pfMod.editId, payload);
        if (!r.ok) throw new Error(r.error || "Edit failed");
        created = r.invoice;
      } else {
        created = await createProForma(payload);
      }
      if (created && created.id != null) {
        // Optimistic in-memory reconcile (durable write already done server-side).
        sInv((arr: any) => [
          created,
          ...((arr || []).filter((i: any) => i.id !== created.id)),
        ]);
        const td2 = new Date().toISOString().slice(0, 10);
        sTe((arr: any) =>
          (arr || []).map((e: any) =>
            pfMod.entryIds.includes(e.id)
              ? {
                  ...e,
                  invoiceId: created.id,
                  invoiceNumber: created.number,
                  billed: true,
                  billedDate: e.billedDate || td2,
                  mark: "billable",
                  billabilityMark: "billable",
                }
              : e,
          ),
        );
      }
      toast(
        `${t("proforma.title", { fallback: "Pro forma invoice" })} ${created?.number || ""} ${t("proforma.saved", { fallback: "saved" })}`,
      );
      onClose();
    } catch (e: any) {
      // 171 §1d — THE SERVER'S OWN SENTENCE, when it has one. The edit route
      // can now refuse a save for a reason only it knows — a source that has
      // been spent since the modal read it (409 `invoice.source_exhausted`),
      // one in another currency (169 R6), one that has left the matter — and
      // "please retry" is the wrong instruction for every one of them. Both
      // exits already resolve `srvErr.<code>` before this point (api.ts's seam
      // for the create, `errText` in invoiceFetch.ts for the edit), so the
      // message is in the reader's language; the generic sentence stays as the
      // fallback for a throw that carries none.
      toast(
        e?.message ||
        t("proforma.save_failed", {
          fallback: "Could not create the pro forma — please retry.",
        }),
        "error",
      );
      setSaving(false);
    }
  };

  // 169 G3 [RULED 2026-09-06] — THE TWO HALVES OF THE CREATE BODY, NAMED, so
  // their ORDER can be a decision instead of a line number. Nothing inside
  // either changed: they are the same elements this function always returned,
  // lifted out of the JSX so the return can put the FORM first when the modal
  // is editing a document (G3) and the PREVIEW first when it is creating one.
  // They are VALUES, not components — no new component identity, nothing for
  // React to remount, and nothing for the Bug-A lint to find.
  const rectoPreview = (
    <>
        {/* === RECTO PREVIEW === */}
        <div
          style={{
            border: "1px solid var(--br)",
            borderRadius: RAD.r10,
            padding: 20,
            marginBottom: 16,
            background: "rgba(255,255,255,.02)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 20,
              gap: 20,
            }}
          >
            <div>
              {logo && (
                <img
                  src={logo}
                  alt=""
                  style={{
                    maxHeight: 36,
                    maxWidth: 140,
                    objectFit: "contain",
                    marginBottom: 8,
                  }}
                />
              )}
              <div style={{ fontSize: FS.s11, fontWeight: 600 }}>
                {firm.name ||
                  t("settings.label.firmName", { fallback: "Firm Name" })}
              </div>
              <div style={{ fontSize: FS.s10, color: "var(--tm)", lineHeight: 1.5 }}>
                {firmA.street && <div>{firmA.street}</div>}
                {(firmA.postalCode || firmA.city) && (
                  <div>
                    {[firmA.postalCode, firmA.city].filter(Boolean).join(" ")}
                  </div>
                )}
                {firmCountryShown ? <div>{String(firmCountryShown)}</div> : null}
                {firm.vat && (
                  <div>
                    {t("proforma.vat_label", { fallback: "VAT" })}: {firm.vat}
                  </div>
                )}
                {firm.phone && <div>{firm.phone}</div>}
                {firm.email && <div>{firm.email}</div>}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontSize: FS.s14,
                  fontWeight: 700,
                  color: "var(--ac)",
                  marginBottom: 6,
                }}
              >
                {t("proforma.header.pro_forma", { fallback: "PRO FORMA" })}
              </div>
              <div
                style={{
                  fontSize: FS.s11,
                  fontFamily: "'JetBrains Mono',monospace",
                  color: "var(--ts)",
                }}
              >
                {/* 171 §1g — THE PREVIEW IS OF THE DOCUMENT BEING EDITED, and
                    it printed the NEXT FREE number. Laurent, on PF-2026/031:
                    "I tick the 31 pro forma with the USD retainer, then tick
                    edit in the modal… and the 33 pro forma appears to be
                    modified." Nothing was: the 33 was `invNumber`, which is
                    max+1 over the year's pro formas and belongs to no document
                    yet. 169 G3 fixed the same lie in the modal's TITLE and
                    this copy of it, one panel below, went on saying 33 — which
                    is worse than the title was, because it sits on a picture
                    of the document. Same cure, same source: the row's own
                    number arrives as `pfMod.remakeNumber`. */}
                {pfMod.remakeNumber || assignedOnSave}
              </div>
              <div style={{ fontSize: FS.s10, color: "var(--tm)", marginTop: 4 }}>
                {t("common.date", { fallback: "Date" })}: {fd(form.invoiceDate)}
              </div>
              <div style={{ fontSize: FS.s10, color: "var(--tm)" }}>
                {t("proforma.due", { fallback: "Due" })}: {fd(form.dueDate)}
              </div>
              {/* 161C §1 — a retainer covers no period (161 §4 hid the SELECTOR
                  and never writes `periodDisplay`, but this header line printed
                  the computed default anyway — "Period: 02/09/2026" on a
                  document that covers nothing). */}
              {docKind === "activities" && (
                <div style={{ fontSize: FS.s10, color: "var(--ts)", marginTop: 4 }}>
                  {t("proforma.period", { fallback: "Period" })}: {periodDisplay}
                </div>
              )}
              <div style={{ fontSize: FS.s10, color: "var(--tm)", marginTop: 4 }}>
                {t("proforma.print.case_label", { fallback: "Case" })}: {cs.ref}
              </div>
            </div>
          </div>
          <div
            style={{
              marginBottom: 16,
              padding: "10px 14px",
              background: "rgba(255,255,255,.03)",
              borderRadius: RAD.r8,
              border: "1px solid var(--br)",
            }}
          >
            <Lbl>{t("proforma.bill_to", { fallback: "Bill to" })}</Lbl>
            {/* 213B §11 [RULED 44] — the boxes, one per client of the case, in its order; the first ticked at opening */}
            {multiClient && (
              <div data-pf-addressees="" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 12px", marginTop: 4 }}>
                <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>{(t as Translate)("proforma.addressees.label", { fallback: "Addressees" })}</span>
                {clientRows.map((c) => (
                  <label key={c.id} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: FS.s10_5, cursor: "pointer" }}>
                    <input type="checkbox" checked={effTicked.includes(c.id)} onChange={() => { setTickedIds(toggleId(effTicked, c.id)); }} />
                    {c.name}
                  </label>
                ))}
              </div>
            )}
            <div style={{ fontSize: FS.s11, fontWeight: 600, marginTop: 4 }}>
              {/* 208 §11 [RULED 9] — the recto previews the document: the title in the client's language, as it is saved;
                  213B §11 — every ticked client, joined, where several are */}
              {multiClient ? addresseeLine : pnameIn(primaryClient, pcLang)}
            </div>
            <div style={{ fontSize: FS.s10, color: "var(--tm)", lineHeight: 1.5 }}>
              {primaryClient.street && <div>{primaryClient.street}</div>}
              {(primaryClient.postalCode || primaryClient.city) && (
                <div>
                  {[primaryClient.postalCode, primaryClient.city]
                    .filter(Boolean)
                    .join(" ")}
                </div>
              )}
              {primaryClient.country && <div>{primaryClient.country}</div>}
              {primaryClient.vat && (
                <div>
                  {t("proforma.vat_label", { fallback: "VAT" })}:{" "}
                  {primaryClient.vat}
                </div>
              )}
            </div>
          </div>
          <div style={{ borderTop: "2px solid var(--br)", paddingTop: 10 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: FS.s11,
                padding: "3px 0",
              }}
            >
              {/* 161C §1 — THE FEES LINE NAMES WHAT THE DOCUMENT IS. A retainer
                  has no activities behind it (R1), so "Fees for professional
                  services (0 min)" described nothing and counted nothing; a
                  credit says which document it credits. Activities keep the
                  exact expression they had, minutes suffix included. */}
              <span>
                {docKind === "activities" ? (
                  <>
                    {t("proforma.fees_for_services", {
                      fallback: "Fees for professional services",
                    })}{" "}
                    ({fmin(totalMinutes)})
                  </>
                ) : docKind === "credit" ? (
                  t("proforma.fees_credit", {
                    number: refundSrc?.number || "—",
                    fallback: `Credit note on ${refundSrc?.number || "—"}`,
                  })
                ) : (
                  t("proforma.fees_retainer", { fallback: "Retainer (provision)" })
                )}
              </span>
              {statedMode ? (
                /* 169 R5 — THE STATED FEE TOTAL: a foreign-currency activities
                   document is priced by this figure (ex-VAT, in the document's
                   currency), typed, in the stated colour (166 R2); the entries
                   still bind for tracking. The server refuses a create without
                   it; `homeFeeTotal` beside it is what the entries yield at the
                   firm's rates — information, never a conversion. */
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  {/* 208 §10 — the one amount field (src/components/AmountField.tsx): « 14 000,00 », typed with a decimal comma; 0 an empty box with a grey « 0 », 0 to the form when emptied (191 fold-in 7, as NumField kept it) */}
                  <AmountField
                    style={{ width: 130, color: "var(--st)", fontWeight: 600, textAlign: "right" }}
                    value={zeroEmpty(form.statedFeeTotal)}
                    empty=""
                    placeholder="0"
                    inputMode="decimal"
                    onAmount={(n) => {
                      uf("statedFeeTotal", n ?? 0);
                    }}
                  />
                  <span style={{ color: "var(--st)", fontWeight: 600 }}>{form.currency}</span>
                  {calc && calc.homeFeeTotal != null && (
                    <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>
                      {t("proforma.stated_fee_hint", { amount: fc(calc.homeFeeTotal), fallback: `Entries at the firm's rates: ${fc(calc.homeFeeTotal)}` })}
                    </span>
                  )}
                </span>
              ) : (
                <span style={{ fontWeight: 600 }}>{fc(feeTotal, form.currency)}</span>
              )}
            </div>
            {/* 161C §1 — NO COSTS LINE on a retainer or a credit. R1 pins the
                ratio out of the arithmetic, so the row read "Costs (19%) €0.00"
                — wrong twice over: it names a ratio that was not applied and a
                cost that does not exist. */}
            {docKind === "activities" && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: FS.s11,
                padding: "3px 0",
                color: "var(--tm)",
              }}
            >
              <span>
                {t("proforma.costs", { fallback: "Costs" })} ({form.costsRatio}%)
              </span>
              <span>{fc(costsAmount, form.currency)}</span>
            </div>
            )}
            {form.commercialDeduction > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: FS.s11,
                  padding: "3px 0",
                  color: "var(--am)",
                }}
              >
                <span>
                  {t("proforma.commercial_deduction", {
                    fallback: "Commercial deduction",
                  })}
                </span>
                <span>-{fc(form.commercialDeduction, form.currency)}</span>
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: FS.s11,
                padding: "3px 0",
                borderTop: "1px solid var(--br)",
                marginTop: 4,
                paddingTop: 6,
              }}
            >
              <span>
                {t("proforma.subtotal_ex_vat", { fallback: "Subtotal ex. VAT" })}
              </span>
              <span style={{ fontWeight: 600 }}>{fc(afterRetainer, form.currency)}</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: FS.s11,
                padding: "3px 0",
                color: "var(--tm)",
              }}
            >
              <span>
                {t("proforma.vat_label", { fallback: "VAT" })} ({form.vatRate}%)
              </span>
              <span>{fc(vatAmount, form.currency)}</span>
            </div>
            {/* Phase 35 Stage-3 r2 (§4) — paid-retainer deduction applies AFTER
              VAT (incl.-VAT advance already paid): row sits below VAT, above the
              total. The math is unchanged — totalToPay already nets it post-VAT.
              161 §5 — the figure is the Σ of the ticked sources now, and the
              ticked numbers are listed under the line. */}
            {effDeduction > 0 && (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: FS.s11,
                    padding: "3px 0",
                    color: "var(--am)",
                  }}
                >
                  <span>
                    {t("proforma.retainer_deduction", {
                      fallback: "Paid retainer deduction incl. VAT",
                    })}
                  </span>
                  <span>-{fc(effDeduction, form.currency)}</span>
                </div>
                {tickedSources.length > 0 && (
                  <div style={{ fontSize: FS.s9, color: "var(--tm)", padding: "0 0 3px 0" }}>
                    {tickedSources.map((s) => s.number).filter(Boolean).join(" · ")}
                  </div>
                )}
              </>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: FS.s13,
                padding: "6px 0",
                fontWeight: 700,
                borderTop: "2px solid var(--br)",
                marginTop: 4,
              }}
            >
              <span>
                {t("proforma.total_to_pay", { fallback: "Total to pay" })}
              </span>
              <span style={{ color: "var(--ac)" }}>{fc(totalToPay, form.currency)}</span>
            </div>
          </div>
          {bank && (
            <div
              style={{
                marginTop: 12,
                padding: "8px 12px",
                background: "rgba(255,255,255,.03)",
                borderRadius: RAD.r6,
                border: "1px solid var(--br)",
                fontSize: FS.s10,
              }}
            >
              <Lbl>{t("proforma.print.bank_account", { fallback: "Bank Account" })}</Lbl>
              <div
                style={{
                  marginTop: 4,
                  fontFamily: "'JetBrains Mono',monospace",
                  color: "var(--ts)",
                }}
              >
                {bank.iban}
              </div>
              {bank.bic && (
                <div style={{ color: "var(--tm)" }}>
                  {t("proforma.print.bic_label", { fallback: "BIC" })}: {bank.bic}
                </div>
              )}
              {bank.bankName && (
                <div style={{ color: "var(--tm)" }}>{bank.bankName}</div>
              )}
            </div>
          )}
          {form.paymentConditions && (
            <div
              style={{
                marginTop: 10,
                fontSize: FS.s10,
                color: "var(--tm)",
                lineHeight: 1.5,
                fontStyle: "italic",
              }}
            >
              {form.paymentConditions}
            </div>
          )}
        </div>
    </>
  );

  const formFields = (
    <>
        {/* === EDITABLE FIELDS === */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 8,
            marginBottom: 12,
          }}
        >
          {/* 161 §4 — a retainer applies no costs ratio (R1) and takes no
              deductions; only the VAT field keeps a job in retainer mode. */}
          {/* 161B §2 — COSTS RATIO AND VAT KEEP THE PLAIN INPUT, deliberately.
              Both boot non-zero and stay non-zero in practice — `costsRatio`
              from the case (`caseCR`, 19 by default) and `vatRate` from the
              firm — so neither is a field a user empties to type into, which is
              the state `value={number}` cannot represent. They are rates, not
              amounts: the stuck zero needs a zero to be stuck ON. Converting
              them would be a change with no defect behind it. */}
          {/* 169 R4 — THE DOCUMENT'S CURRENCY, chosen at issue and never changed:
              the firm's first and selected, the rest of what this runtime knows;
              read-only on an EDIT (the document's). */}
          <div className="field">
            <label>{t("proforma.field.currency", { fallback: "Currency" })}</label>
            {pfMod.editId ? (
              <span className="field-input" style={{ display: "inline-block", width: 80 }}>{form.currency}</span>
            ) : (
              <MenuSelect
                className="field-input"
                style={{ width: 90 }}
                value={form.currency}
                onChange={(v) => uf("currency", v)}
                options={[[firmCur, firmCur] as const, ...PF_CURRENCY_OPTIONS.filter((o) => o[0] !== firmCur)]}
                ariaLabel={t("proforma.field.currency", { fallback: "Currency" })}
              />
            )}
          </div>
          {!isRetainer && (
          <div className="field">
            <label>
              {t("proforma.field.costs_ratio", { fallback: "Costs Ratio %" })}
            </label>
            {/* 191 fold-in 7 — the two pre-161B number inputs of this modal
                (`value={aNumber}` + `+e.target.value || 0`, the stuck zero
                at clear AND at open) join NumField: the text is the field's,
                the number the form's, a 0 an empty box with the grey "0".
                min/max/step ride as they were; no clamp is added (none stood
                here). */}
            <NumField
              min={0}
              max={100}
              style={{ width: 80 }}
              value={form.costsRatio}
              onCommit={(n) => uf("costsRatio", n)}
            />
          </div>
          )}
          <div className="field">
            <label>{t("proforma.field.vat_pct", { fallback: "VAT %" })}</label>
            <NumField
              min={0}
              max={100}
              step={0.5}
              style={{ width: 80 }}
              value={form.vatRate}
              onCommit={(n) => uf("vatRate", n)}
            />
          </div>
          {/* 161 §5 — the manual "Retainer deduction €" field is GONE (R3): the
              deduction is chosen by checkbox from the matter's sources, in the
              block right under this grid. */}
          {/* Phase 35 Stage-3 — Commercial deduction; behaves like the old
              retainer field did (pre-VAT reduction). Replaces the credits modal. */}
          {!isRetainer && (
          <div className="field">
            <label>
              {/* 174 §7 — ONE SHAPE FOR THE THREE SIBLING FIELD LABELS. Laurent:
                  "DÉDUCTION HTVA €" vs "REMISE COMMERCIALE USD" — they disagree
                  in shape and in currency placement. MEASURED: the two siblings
                  in this modal read `<name> {currency} HTVA`
                  (`proforma.retainer_amount_label`,
                  `proforma.stated_fee_label`) and this one read `<name>
                  {currency}` — with the VAT basis missing though the amount is
                  subtracted ABOVE the "Sous-total HTVA" line (:1571) and is
                  therefore ex-VAT exactly as they are. AND its euro form
                  `proforma.commercial_deduction_label` had NO SEED ROW IN ANY
                  LANGUAGE, so a French reader on a euro firm read "Commercial
                  deduction €" off the English fallback.
                  A NEW key rather than a rewritten one: the seed only INSERTS
                  (105 §5), so a changed string needs a new key or it never
                  reaches a box that already ran the old one. */}
              {curLabel("proforma.commercial_deduction_exvat", "Commercial deduction € ex. VAT", "Commercial deduction {currency} ex. VAT")}
            </label>
            {/* 161B §2 — CORRECTION TO THE BRIEF'S PREMISE, measured: this field
                does NOT have a non-zero default. `commercialDeduction: 0`
                (:1937 block) — it is the retainer-amount case exactly, a
                zero-default money field a user must clear to type into, so it
                hit the same wall and takes the same fix. Costs ratio and VAT
                above are the ones the brief's description actually fits. */}
            {/* 208 §10 — the one amount field (src/components/AmountField.tsx): « 14 000,00 », typed with a decimal comma; 0 an empty box with a grey « 0 », 0 to the form when emptied (191 fold-in 7, as NumField kept it) */}
            <AmountField
              style={{ width: 100 }}
              value={zeroEmpty(form.commercialDeduction)}
              empty=""
              placeholder="0"
              inputMode="decimal"
              onAmount={(n) => {
                uf("commercialDeduction", n ?? 0);
              }}
            />
          </div>
          )}
        </div>
        {/* 161 §5 — THE SOURCES, where the manual deduction field stood: one
            line per source document still holding paid money — checkbox ·
            number · kind · unconsumed · the amount to apply. The prefill is
            min(unconsumed, what this document's gross can still absorb after
            the sources already ticked); editable DOWNWARD only. Renders
            nothing when the matter holds no source. 212 §22 — and when they
            could not be read, their heading and the mark, with « Réessayer ». */}
        {sourcesActive && srcRead.failure && (
          <div style={{ border: "1px solid var(--br)", borderRadius: RAD.r8, padding: "8px 12px", marginBottom: 12 }}>
            <Lbl>
              {(t as Translate)("proforma.sources_heading", {
                fallback: "Paid retainers held on this matter",
              })}
            </Lbl>
            <ListLoadContext.Provider value={srcLists}>
              <ListNotLoaded lists={[PAGE_LISTS.invoiceSources]} variant="inline" />
            </ListLoadContext.Provider>
          </div>
        )}
        {sourcesActive && (sourceDocs || []).length > 0 && (
          <div
            style={{
              border: "1px solid var(--br)",
              borderRadius: RAD.r8,
              padding: "8px 12px",
              marginBottom: 12,
            }}
          >
            <Lbl>
              {t("proforma.sources_heading", {
                fallback: "Paid retainers held on this matter",
              })}
            </Lbl>
            {(sourceDocs || []).map((s) => {
              const sel = srcSel[s.id] || { on: false, amount: 0 };
              const ceiling = srcCeiling(s.id);
              return (
                <div
                  key={s.id}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", fontSize: FS.s10_5 }}
                >
                  <input
                    type="checkbox"
                    checked={sel.on}
                    onChange={(e) =>
                      setSrcSel((p) => ({
                        ...p,
                        [s.id]: e.target.checked
                          ? { on: true, amount: srcCeiling(s.id) }
                          : { on: false, amount: 0 },
                      }))
                    }
                  />
                  <span style={{ fontFamily: "'JetBrains Mono',monospace", color: "var(--ac)", fontWeight: 600 }}>
                    {s.number}
                  </span>
                  <span style={{ color: "var(--tm)" }}>
                    {s.docKind === "retainer"
                      ? t("invoice.kind.retainer", { fallback: "Retainer" })
                      : s.docKind === "credit"
                        ? t("invoice.kind.credit", { fallback: "Credit" })
                        : t("invoice.status.cancelled", { fallback: "Cancelled" })}
                  </span>
                  <span style={{ color: "var(--ts)", marginLeft: "auto" }}>
                    {fc(s.unconsumed, s.currency)}
                  </span>
                  {/* 161B §2 — the same wall, and the brief names it: this one
                      PREFILLS, so a user clearing it to type a smaller draw hit
                      the stuck zero exactly as the retainer field did. Same
                      component, same clamp (the ceiling), applied per keystroke
                      and again on blur. */}
                  {sel.on && (
                    <AmountField
                      style={{ width: 110, margin: 0 }}
                      value={zeroEmpty(sel.amount)}
                      empty=""
                      placeholder="0"
                      inputMode="decimal"
                      max={ceiling}
                      onAmount={(v) => {
                        setSrcSel((p) => ({ ...p, [s.id]: { on: true, amount: v ?? 0 } }));
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
        {/* Phase 35 (C1 / L2) — manual pro-forma-number override. Shown only when
          the firm has enabled it in Settings; otherwise the number is allocated
          server-side on save (the provisional number above is a preview). */}
        {allowManual && (
          <div className="field" style={{ marginBottom: 12 }}>
            <label>
              {t("proforma.number_label", { fallback: "Pro forma number" })}
            </label>
            <input
              {...NO_AUTOFILL}
              className={noAutofillClass("field-input")}
              type="text"
              value={form.numberOverride}
              placeholder={assignedOnSave}
              onChange={(e) => uf("numberOverride", e.target.value)}
            />
          </div>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            marginBottom: 12,
          }}
        >
          <div className="field">
            <label>
              {t("proforma.field.invoice_date", { fallback: "Invoice Date" })}
            </label>
            <input
              className="field-input"
              type="date"
              value={form.invoiceDate}
              onChange={(e) => uf("invoiceDate", e.target.value)}
            />
          </div>
          <div className="field">
            <label>{t("field.due_date", { fallback: "Due Date" })}</label>
            <input
              className="field-input"
              type="date"
              value={form.dueDate}
              onChange={(e) => uf("dueDate", e.target.value)}
            />
          </div>
        </div>
        {/* 161 §4 — a retainer covers no period: the selector is hidden and
            `periodDisplay` is never written (the list's Période cell shows the
            document kind instead). */}
        {!isRetainer && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: FS.s10, fontWeight: 600, color: "var(--tm)" }}>
            {t("proforma.period_covered", { fallback: "Period Covered" })}
          </span>
          <SegmentedControl
            value={form.periodMode}
            options={[
              ["exact", t("proforma.period_mode.exact", { fallback: "Exact" })],
              ["custom", t("proforma.period_mode.custom", { fallback: "Custom" })],
              ["month", t("proforma.period_mode.month", { fallback: "Month" })],
              [
                "quarter",
                t("proforma.period_mode.quarter", { fallback: "Quarter" }),
              ],
            ]}
            onChange={(k: any) => uf("periodMode", k)}
          />
          {form.periodMode === "custom" && (
            <>
              <input
                className="field-input"
                type="date"
                style={{ width: 130, margin: 0 }}
                value={form.periodFrom}
                onChange={(e) => uf("periodFrom", e.target.value)}
              />
              <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>—</span>
              <input
                className="field-input"
                type="date"
                style={{ width: 130, margin: 0 }}
                value={form.periodTo}
                onChange={(e) => uf("periodTo", e.target.value)}
              />
            </>
          )}
          <span style={{ fontSize: FS.s10, color: "var(--ts)", fontStyle: "italic" }}>
            {periodDisplay}
          </span>
        </div>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            marginBottom: 12,
          }}
        >
          <div className="field">
            <label>
              {t("proforma.print.bank_account", { fallback: "Bank Account" })}
            </label>
            <MenuSelect
              className="field-input"
              value={String(form.bankIdx)}
              onChange={(v) => uf("bankIdx", +v)}
              options={[
                ...(banks.length === 0
                  ? [
                      [
                        "0",
                        t("proforma.no_banks", {
                          fallback: "— No bank accounts configured —",
                        }),
                      ] as const,
                    ]
                  : []),
                ...banks.map(
                  (b: any, i: any) =>
                    [String(i), String(b.name || b.iban || "")] as const,
                ),
              ]}
            />
          </div>
          {/* 169 G4 [RULED 2026-09-06] — THE "GC LANGUAGE" FIELD IS GONE. A
              document's language is the CLIENT's, and it is set in the client
              record. This select wrote `generalConditionsLang`, whose ONLY
              reader is docGen.js:784 — it chooses which language's
              general-conditions text prints — so its whole effect was to let
              one block of a document speak a different language from the rest
              of it. With nothing writing the column, `gcLang` falls through to
              the document language `pickDocumentLanguage` resolves from
              `Client.language` (docGen.js:629), and the conditions follow the
              document. The COLUMN STAYS: existing rows keep their value and
              keep printing it. */}
        </div>
        <div className="field">
          {/* Phase 35 Stage-3 — "Particular conditions" (was Payment conditions);
              prefilled with the case rate + success fee + payment delay, fully
              editable, and also carries the commercial-deduction explanation. */}
          <label>
            {t("proforma.particular_conditions", {
              fallback: "Particular conditions",
            })}
          </label>
          <textarea
            {...NO_AUTOFILL}
            className={noAutofillClass("field-input")}
            rows={3}
            style={{ resize: "vertical", fontSize: FS.s10_5 }}
            value={form.paymentConditions}
            onChange={(e) => uf("paymentConditions", e.target.value)}
          />
        </div>
        <div className="field">
          <label>{t("common.notes", { fallback: "Notes" })}</label>
          <textarea
            {...NO_AUTOFILL}
            className={noAutofillClass("field-input")}
            rows={2}
            style={{ resize: "vertical", fontSize: FS.s10_5 }}
            value={form.notes}
            onChange={(e) => uf("notes", e.target.value)}
          />
        </div>
    </>
  );

  return (
    <ModalLayout
      title={
        // 169 G3 [RULED 2026-09-06] — AN EDIT IS NAMED FOR THE DOCUMENT IT
        // EDITS. `invNumber` is the NEXT FREE number, so editing PF-2026/031
        // announced "Create Pro Forma Invoice — PF-2026/033": a number that
        // does not exist yet, on a screen about one that does. The document's
        // own number arrives as `pfMod.remakeNumber`.
        pfMod.editId
          ? t("proforma.title_edit_doc", { fallback: "Edit pro forma {number}" })
              .replace("{number}", String(pfMod.remakeNumber || ""))
          : isRetainer
            ? /* 179 C.9 — no predicted number in a create title: NEW keys (the
                 seed only inserts, so the numbered ones stay for the edit arm). */
              docKind === "credit"
                ? t("proforma.title_credit_new", { fallback: "Credit pro forma" })
                : t("proforma.title_retainer_new", { fallback: "Invoice a retainer" })
            : /* 171 §1f — THE THREE STATES ARE ONE FAMILY, and the title is
                 how a reader tells them apart at a glance: "Pro forma X"
                 (view) · "Edit pro forma X" (edit) · "Create pro forma X"
                 (create). A NEW key rather than a reworded one: the seed only
                 INSERTS, so rewriting `proforma.title_create` would have
                 changed nothing on any seeded box. */
              t("proforma.title_create_new", { fallback: "Create pro forma invoice" })
      }
      onClose={onClose}
      maxWidth={720}
      footer={
        <div className="modal-footer" style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
          {banks.length === 0 && (
            <p style={{ fontSize: FS.s11, color: "var(--rd)", margin: 0, textAlign: "center" }}>
              {t("proforma.no_bank_account", { fallback: "Configure at least one bank account in Settings before creating a pro forma." })}
            </p>
          )}
          {/* 160 §2 — THE INNER FLEX ROW IS GONE, and that is the whole change.
              `.modal-footer` is `flex-direction: column; align-items: stretch`
              (App.css), so a footer that simply lists its buttons gets the
              house arrangement for free: each on its own full-width row,
              Cancel first. This one wrapped the pair in a horizontal row and
              defeated that, which is why Cancel sat inline-left of a `flex: 1`
              Save — the one such footer in the tree (census: 17 footers put
              CancelButton/modal-cancel first, 1 does not, and that one is a
              conflict dialog whose two actions are both forward). The Save's
              `flex: 1` went with the row it was filling. */}
          <CancelButton onClick={onClose} />
          {/* Phase 35 Stage-3 — a single Save (no draft/ready; no Send to
              Accounting). The pro forma is created active (unpaid). Phase 40 —
              blocked when no bank account is configured (server also rejects). */}
          <SaveButton
            label={t("proforma.save_proforma", {
              fallback: "Save pro forma invoice",
            })}
            icon={null}
            onClick={() => saveInvoice()}
            // C.2 (BRIEF 04) — Save waits for the first /compute response:
            // the old client always had ladder values at save time; without
            // this gate a compute outage (or an API not yet serving the
            // route) would let a pro forma save with no money computed
            // anywhere. The new server recomputes on create/edit regardless;
            // this only closes the torn-window/outage case.
            disabled={
              saving || banks.length === 0 || !calc
              // 209 §5 — no language to write the title and conditions in
              || pcLangOf == null
              || (isRetainer && !(Number(form.retainerAmount) > 0))
              // 169 R5 — a foreign activities document needs its stated total.
              || (statedMode && !(Number(form.statedFeeTotal) > 0))
              || (docKind === "credit"
                && (refundSourceId == null
                  || Number(form.retainerAmount) > refundExOf(refundSrc?.unconsumed ?? 0)))
            }
            style={{
              padding: "10px 16px",
              fontSize: FS.s12,
              background: "rgba(52,211,153,.15)",
              color: "var(--gn)",
              border: "1px solid rgba(52,211,153,.35)",
            }}
          />
        </div>
      }
    >
      {/* 169 G1 [RULED by Laurent at the 2026-09-06 glance] — THE RETAINER
          MODAL PUTS ITS FIELDS FIRST. Measured create order before this move:
          recto preview, editable fields, sources, period, dates/bank/GC/
          conditions/notes, and the retainer block LAST — so invoicing a
          retainer opened on a preview reading 0,00 and the one number that
          makes the document was reached by scrolling past everything else.
          In RETAINER mode the block now comes FIRST: what is typed leads, the
          preview below it is the confirmation. It renders only when
          `isRetainer`, so the ACTIVITIES order is byte-for-byte unmoved — and
          that arm is deliberately NOT flipped, because 169 put the stated-fee
          input inside the preview's fees line, which already puts its required
          input at the top. Laurent rules on that arm at the next glance. */}
      {/* 209 §5 [RULED 18] — neither the client nor its case has a language:
          nothing is written, the case form is one click away. */}
      {pcLangOf == null && (
        <div
          style={{
            display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            padding: "8px 12px", marginBottom: 12, borderRadius: RAD.r8,
            border: "1px solid var(--am)", color: "var(--am)", fontSize: FS.s11, fontWeight: 600,
          }}
        >
          <span>{t("srvErr.case.language_required", { fallback: "Choose the case's language first" })}</span>
          <button
            type="button"
            className="btn"
            style={{ padding: "3px 10px", fontSize: FS.s10 }}
            onClick={() => {
              onClose();
              openMatterForm(csType === "cc" ? { ccId: Number(csId) } : { caseId: Number(csId) });
            }}
          >
            {t("case.language.open_form", { fallback: "Open the case form" })}
          </button>
        </div>
      )}
      {/* 161 §4 — in retainer mode the ANNEXE is REPLACED by the one money
          field: there are no time entries behind a retainer (R1), so a detail
          table has nothing to detail. Ex-VAT, required, > 0 — the Save button
          refuses until it is. */}
      {isRetainer && (
        <div
          style={{
            borderBottom: "1px solid var(--br)",
            marginBottom: 16,
            paddingBottom: 12,
          }}
        >
          <div className="field">
            <label>
              {curLabel("proforma.retainer_amount_label", "Retainer amount € ex. VAT", "Retainer amount {currency} ex. VAT")}
            </label>
            {/* 161B §2 — the field owns its text (NumField, module scope);
                clearing it no longer re-renders a 0 for the caret to land
                behind. The §6 ceiling is unchanged, handed over as `clamp` so
                it applies on every keystroke AND on blur exactly as the old
                inline onChange applied it. */}
            {/* 208 §10 — the one amount field (src/components/AmountField.tsx): « 14 000,00 », typed with a decimal comma; 0 an empty box with a grey « 0 », 0 to the form when emptied (191 fold-in 7, as NumField kept it) */}
            <AmountField
              style={{ width: 160 }}
              value={zeroEmpty(form.retainerAmount)}
              empty=""
              placeholder="0"
              inputMode="decimal"
              // §6 — in refund mode the amount is editable DOWNWARD only:
              // its ceiling is the picked source's unconsumed balance,
              // converted to this document's ex-VAT domain.
              max={refundMode && refundSrc ? refundExOf(refundSrc.unconsumed) : undefined}
              onAmount={(n) => {
                uf("retainerAmount", n ?? 0);
              }}
              autoFocus
            />
          </div>
          {/* 161 §6 — REIMBURSEMENT: the same modal, sign flipped. Ticked, it
              picks ONE §5 source and gives its unconsumed balance (or less)
              back as a negative, numbered, listed document (R4). */}
          <label
            style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: FS.s10_5, cursor: "pointer", marginBottom: 8 }}
          >
            <input
              type="checkbox"
              checked={refundMode}
              onChange={(e) => {
                setRefundMode(e.target.checked);
                if (!e.target.checked) setRefundSourceId(null);
              }}
            />
            {t("proforma.credit", { fallback: "Credit note" })}
          </label>
          {refundMode && (
            <div className="field">
              <label>
                {t("proforma.credit_source_label", { fallback: "Document to credit" })}
              </label>
              <ListLoadContext.Provider value={srcLists}>
              <ListNotLoaded lists={[PAGE_LISTS.invoiceSources]} variant="inline">
              {(sourceDocs || []).length === 0 ? (
                <p style={{ fontSize: FS.s10, color: "var(--tm)", margin: 0 }}>
                  {t("proforma.credit_no_source", {
                    fallback: "No paid document on this matter can be credited.",
                  })}
                </p>
              ) : (
                <MenuSelect
                  className="field-input"
                  value={refundSourceId != null ? String(refundSourceId) : ""}
                  onChange={(v) => {
                    const src = (sourceDocs || []).find((s) => s.id === +v) || null;
                    setRefundSourceId(src ? src.id : null);
                    if (src) uf("retainerAmount", refundExOf(src.unconsumed));
                  }}
                  options={(sourceDocs || []).map((s) => [
                    String(s.id),
                    `${s.number || s.id} — ${fc(s.unconsumed, s.currency)}`,
                  ] as const)}
                />
              )}
              </ListNotLoaded>
              </ListLoadContext.Provider>
            </div>
          )}
        </div>
      )}
      {/* 169 G3 [RULED 2026-09-06] — EDIT IS THE CREATE FORM, ON THE DOCUMENT,
          and a form you are correcting must lead. Editing put the recto
          preview first, so the fields a person came to change were below a
          picture of the document — the same complaint G1 answered for the
          retainer arm, in the arm where it costs more. CREATE keeps the
          preview first, byte-for-byte. */}
      {pfMod.editId ? (
        <>
          {formFields}
          {rectoPreview}
        </>
      ) : (
        <>
          {rectoPreview}
          {formFields}
        </>
      )}

      {/* === ATTACHMENT PREVIEW === */}
      {!isRetainer && (
      <div
        style={{
          borderTop: "1px solid var(--br)",
          marginTop: 16,
          paddingTop: 12,
        }}
      >
        <Lbl>
          {t("proforma.print.attachment_title", {
            fallback: "Attachment — Time Entry Detail",
          })}
        </Lbl>
        <Table
          style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}
        >
          <thead>
            <tr style={{ borderBottom: "1px solid var(--br)" }}>
              <th
                style={{
                  textAlign: "left",
                  fontSize: FS.s9,
                  color: "var(--tm)",
                  padding: "3px 4px",
                }}
              >
                {t("proforma.print.col_date", { fallback: "Date" })}
              </th>
              <th
                style={{
                  textAlign: "left",
                  fontSize: FS.s9,
                  color: "var(--tm)",
                  padding: "3px 4px",
                }}
              >
                {t("proforma.print.col_activity", { fallback: "Activity" })}
              </th>
              <th
                style={{
                  textAlign: "left",
                  fontSize: FS.s9,
                  color: "var(--tm)",
                  padding: "3px 4px",
                }}
              >
                {t("proforma.print.col_description", {
                  fallback: "Description",
                })}
              </th>
              <th
                style={{
                  textAlign: "right",
                  fontSize: FS.s9,
                  color: "var(--tm)",
                  padding: "3px 4px",
                }}
              >
                {t("proforma.print.col_minutes", { fallback: "Minutes" })}
              </th>
            </tr>
          </thead>
          <tbody>
            {[...entries]
              .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
              .map((e) => (
                <tr
                  key={e.id}
                  style={{ borderBottom: "1px solid rgba(255,255,255,.04)" }}
                >
                  <td
                    style={{
                      fontSize: FS.s10,
                      padding: "3px 4px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fd(e.date)}
                  </td>
                  <td
                    style={{
                      fontSize: FS.s10,
                      padding: "3px 4px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {actName(e.type)}
                  </td>
                  <td
                    style={{
                      fontSize: FS.s10,
                      padding: "3px 4px",
                      color: "var(--ts)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: 300,
                    }}
                  >
                    {e.desc || "—"}
                  </td>
                  <td
                    style={{
                      fontSize: FS.s10,
                      padding: "3px 4px",
                      textAlign: "right",
                      fontFamily: "'JetBrains Mono',monospace",
                    }}
                  >
                    {e.min || 0}
                  </td>
                </tr>
              ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: "1px solid var(--br)" }}>
              <td
                colSpan={3}
                style={{ fontSize: FS.s10, padding: "4px", fontWeight: 600 }}
              >
                {t("proforma.total", { fallback: "Total" })}
              </td>
              <td
                style={{
                  fontSize: FS.s10,
                  padding: "4px",
                  textAlign: "right",
                  fontWeight: 600,
                  fontFamily: "'JetBrains Mono',monospace",
                }}
              >
                {totalMinutes} {t("proforma.min_unit", { fallback: "min" })} (
                {fmin(totalMinutes)})
              </td>
            </tr>
          </tfoot>
        </Table>
      </div>
      )}
    </ModalLayout>
  );
}
